/**
 * Zento AI Embeddable Chat Widget
 * 
 * A floating chat bubble widget that can be embedded on any website.
 * Communicates with the Zento AI API to provide AI-powered chat support.
 */

import { marked } from 'marked';
import type { Token } from 'marked';
import DOMPurify from 'dompurify';
import { createStyles, createTicketPdfOverlayStyles } from './styles';
import { icons } from './icons';
import {
  WidgetApiClient,
  AssistantInfo,
  ChatMessage,
  SendMessageResponse,
  ConversationSummary,
  FileAttachment,
  PageContext,
  WidgetUserData,
  WidgetTicketSummary,
  WidgetDevTimeMonthRow,
  generateSessionId,
  persistSessionIdForApiKey,
  storeConversationId,
  WIDGET_CONVERSATIONS_PAGE_SIZE,
  WIDGET_TICKETS_PAGE_SIZE,
  WIDGET_MESSAGES_PAGE_SIZE,
} from './api-client';
import { formatDevelopmentMinutes } from '@/utils/format';
import {
  subscribeConversationRealtime,
  subscribeSessionRealtime,
  SessionMessage,
} from './use-widget-realtime';
import { sortChatMessagesForDisplay } from '@/utils/chat-message-order';
import { sanitizeWidgetHtmlForChat, isWidgetHtmlMessage, stripWidgetHtmlPrefix } from '@/utils/widget-message-content';

/** Public Zento API. Never inferred from the host page’s origin — sites running on localhost still use production unless `baseUrl` is set. */
const DEFAULT_WIDGET_API_BASE_URL = 'https://zento-ai-bb12f3ac2bd1.herokuapp.com';

type WidgetInboxTab = 'chats' | 'tickets';

const TICKET_STATUS_LABELS: Record<string, string> = {
  abierto: 'Abierto',
  en_progreso: 'En progreso',
  en_espera: 'En espera',
  resuelto: 'Resuelto',
  cerrado: 'Cerrado',
};

export interface ZentoWidgetConfig {
  /** The API key for your Zento AI assistant */
  apiKey: string;
  /** Base URL for the API (defaults to production). Set explicitly when pointing at a local/staging backend. */
  baseUrl?: string;
  /** Widget position on the page */
  position?: 'bottom-right' | 'bottom-left';
  /** Whether to show the widget initially open */
  initiallyOpen?: boolean;
  /** Custom greeting to show before welcome message */
  customGreeting?: string;
  /** Z-index for the widget (default: 2147483647) */
  zIndex?: number;
  /** Whether to show debug info panel (files used, timing, etc.) */
  showDebug?: boolean;
  /** Callback when widget is opened */
  onOpen?: () => void;
  /** Callback when widget is closed */
  onClose?: () => void;
  /** Callback when a message is sent */
  onMessageSent?: (message: string) => void;
  /** Callback when a response is received */
  onMessageReceived?: (message: string) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
  /**
   * Signed JWT from your backend (HS256 with the widget identity secret) or a function that returns it.
   * When set, the widget exchanges it for a stable session id and optional name/email claims.
   */
  identityToken?: string | (() => Promise<string | null>);
}

type ResolvedZentoWidgetConfig = Required<Omit<ZentoWidgetConfig, 'identityToken'>> & {
  identityToken?: ZentoWidgetConfig['identityToken'];
};

interface MessageWithDebug extends ChatMessage {
  debug?: SendMessageResponse['debug'];
  response_time_ms?: number;
  attachments?: WidgetAttachment[];
}

// Widget-specific attachment with preview URL
interface WidgetAttachment extends FileAttachment {
  preview_url?: string;
}

interface WidgetState {
  isOpen: boolean;
  isLoading: boolean;
  isTyping: boolean;
  messages: MessageWithDebug[];
  error: string | null;
  conversationId: string | null;
  assistantInfo: AssistantInfo | null;
  showConversationList: boolean;
  conversations: ConversationSummary[];
  isLoadingConversations: boolean;
  forceNewConversation: boolean;
  attachments: WidgetAttachment[];
  // Human request state
  showHumanRequestModal: boolean;
  humanRequested: boolean;
  widgetUser: WidgetUserData | null;
  // Human agent mode - when conversation is assigned to human
  isHumanMode: boolean;
  assignedTo: string | null;
  // Unread messages state
  totalUnreadCount: number;
  // Inbox tabs (chats / tickets when email matches a CRM client)
  inboxTab: WidgetInboxTab;
  hasLinkedClient: boolean;
  linkedClientName: string | null;
  showDevelopmentTime: boolean;
  clientTickets: WidgetTicketSummary[];
  isLoadingClientTickets: boolean;
  devTimeByMonth: WidgetDevTimeMonthRow[];
  isLoadingDevTimeByMonth: boolean;
  selectedDevTimeMonth: string;
  devTimeMonthPickerOpen: boolean;
}


export class ZentoWidget {
  private config: ResolvedZentoWidgetConfig;
  private apiClient: WidgetApiClient;
  private sessionId: string;
  private state: WidgetState;
  private container: HTMLDivElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private widgetRoot: HTMLDivElement | null = null;
  private chatWindow: HTMLDivElement | null = null;
  private messagesContainer: HTMLDivElement | null = null;
  private inputElement: HTMLTextAreaElement | null = null;
  private inputContainer: HTMLDivElement | null = null;
  private humanRequestButton: HTMLButtonElement | null = null;
  private humanRequestText: HTMLSpanElement | null = null;
  private conversationListPanel: HTMLDivElement | null = null;
  private conversationItemsContainer: HTMLDivElement | null = null;
  private ticketItemsContainer: HTMLDivElement | null = null;
  private ticketDevUsageContainer: HTMLDivElement | null = null;
  private inboxTabsContainer: HTMLDivElement | null = null;
  private inboxChatsToolbar: HTMLDivElement | null = null;
  private ticketPdfOverlay: HTMLDivElement | null = null;
  private ticketPdfStyleElement: HTMLStyleElement | null = null;
  private ticketPdfObjectUrl: string | null = null;
  private ticketPdfEscapeHandler: ((event: KeyboardEvent) => void) | null = null;
  private ticketPdfBodyOverflowPrevious: string | null = null;
  /** Floating “Ir al final” when scrolled away from latest messages */
  private jumpToBottomButton: HTMLButtonElement | null = null;
  private jumpToBottomResizeObserver: ResizeObserver | null = null;
  private styleElement: HTMLStyleElement | null = null;
  private fileInputElement: HTMLInputElement | null = null;
  private attachmentPreviewContainer: HTMLDivElement | null = null;
  // Subscription for real-time messages (conversation-level)
  private subscriptionCleanup: (() => void) | null = null;
  // Session-level subscription for global notifications
  private sessionSubscriptionCleanup: (() => void) | null = null;
  /** Dedupe session + conversation realtime duplicates (same message broadcast twice) */
  private recentNotificationDedupeKeys: string[] = [];
  /** Prevents concurrent open() while identityToken is resolving */
  private openIdentityResolveInProgress = false;

  /** Conversation list pagination (aligned with inbox: offset + has_more) */
  private conversationsOffset = 0;
  private conversationsHasMore = false;
  private isLoadingMoreConversations = false;
  private conversationListLoadMoreObserver: IntersectionObserver | null = null;

  /** Ticket list pagination */
  private ticketsOffset = 0;
  private ticketsHasMore = false;
  private isLoadingMoreTickets = false;
  private ticketListLoadMoreObserver: IntersectionObserver | null = null;
  private devTimeMonthPickerOutsideHandler: ((event: MouseEvent) => void) | null = null;

  /** Message thread pagination */
  private messagesOffset = 0;
  private messagesHasMore = false;
  private isLoadingMoreMessages = false;
  /** Avoid auto load-more until initial scroll-to-bottom window (like inbox) */
  private canTriggerMessageLoadMore = false;

  constructor(config: ZentoWidgetConfig) {
    if (!config.apiKey) {
      throw new Error('[ZentoWidget] apiKey is required');
    }

    // Set defaults
    this.config = {
      apiKey: config.apiKey,
      baseUrl: config.baseUrl || DEFAULT_WIDGET_API_BASE_URL,
      position: config.position || 'bottom-right',
      initiallyOpen: config.initiallyOpen || false,
      customGreeting: config.customGreeting || '',
      zIndex: config.zIndex || 2147483647,
      showDebug: config.showDebug || false,
      onOpen: config.onOpen || (() => { }),
      onClose: config.onClose || (() => { }),
      onMessageSent: config.onMessageSent || (() => { }),
      onMessageReceived: config.onMessageReceived || (() => { }),
      onError: config.onError || (() => { }),
      identityToken: config.identityToken,
    };

    this.apiClient = new WidgetApiClient({
      apiKey: this.config.apiKey,
      baseUrl: this.config.baseUrl,
    });

    console.log(`[ZentoWidget] Using backend: ${this.config.baseUrl}`);

    this.sessionId = generateSessionId(this.config.apiKey);

    this.state = {
      isOpen: this.config.initiallyOpen,
      isLoading: true,
      isTyping: false,
      messages: [],
      error: null,
      conversationId: null, // Don't restore from localStorage - always start fresh
      assistantInfo: null,
      showConversationList: true, // Always show conversation list on open
      conversations: [],
      isLoadingConversations: false,
      forceNewConversation: false,
      attachments: [],
      // Human request state
      showHumanRequestModal: false,
      humanRequested: false,
      widgetUser: this.loadWidgetUser(),
      // Human agent mode
      isHumanMode: false,
      assignedTo: null,
      // Unread messages
      totalUnreadCount: 0,
      inboxTab: 'chats',
      hasLinkedClient: false,
      linkedClientName: null,
      showDevelopmentTime: false,
      clientTickets: [],
      isLoadingClientTickets: false,
      devTimeByMonth: [],
      isLoadingDevTimeByMonth: false,
      selectedDevTimeMonth: ZentoWidget.currentMonthStartIso(),
      devTimeMonthPickerOpen: false,
    };

    // Configure marked with custom renderer
    this.configureMarked();
  }

  /**
   * Configure marked with GFM support
   * We intentionally mimic Playground behavior (react-markdown + remark-gfm):
   * - GFM enabled
   * - Soft line breaks are NOT converted to <br> by default (breaks: false)
   * - Raw HTML is NOT rendered (we escape it) to avoid XSS and match Playground safety
   */
  private configureMarked(): void {
    const escapeHtml = (text: string) => this.escapeHtml(text);

    const sanitizeHref = (href: string | null | undefined): string | null => {
      if (!href) return null;
      const raw = href.trim();
      if (!raw) return null;

      // Allow relative and hash links
      if (raw.startsWith('/') || raw.startsWith('#')) return raw;

      // Allow common safe protocols
      try {
        const url = new URL(raw, 'https://example.invalid');
        const protocol = url.protocol.toLowerCase();
        if (protocol === 'http:' || protocol === 'https:' || protocol === 'mailto:' || protocol === 'tel:') {
          return raw;
        }
      } catch {
        // If it's not a valid URL, treat it as unsafe
      }

      return null;
    };

    // Set global marked options for this widget instance
    marked.setOptions({
      gfm: true,
      breaks: false,
    });

    // Override renderer output to match Playground semantics + widget CSS classes
    marked.use({
      renderer: {
        // Block raw HTML (match react-markdown default behavior)
        html: function (token) {
          return escapeHtml(token.text);
        },

        heading: function (token) {
          const level = token.depth;
          const cls =
            level === 1 ? 'zento-md-h1' :
              level === 2 ? 'zento-md-h2' :
                level === 3 ? 'zento-md-h3' :
                  'zento-md-h4';
          const inner = this.parser.parseInline(token.tokens);
          return `<h${level} class="${cls}">${inner}</h${level}>`;
        },

        paragraph: function (token) {
          const inner = this.parser.parseInline(token.tokens);
          return `<p class="zento-md-p">${inner}</p>`;
        },

        strong: function (token) {
          return `<strong>${this.parser.parseInline(token.tokens)}</strong>`;
        },

        em: function (token) {
          return `<em>${this.parser.parseInline(token.tokens)}</em>`;
        },

        del: function (token) {
          return `<del>${this.parser.parseInline(token.tokens)}</del>`;
        },

        blockquote: function (token) {
          return `<blockquote class="zento-md-blockquote">${this.parser.parse(token.tokens)}</blockquote>`;
        },

        codespan: function (token) {
          return `<code class="zento-md-inline-code">${escapeHtml(token.text)}</code>`;
        },

        code: function (token) {
          const language = (token.lang || '').trim().split(/\s+/)[0];
          const langClass = language ? ` class="language-${escapeHtml(language)}"` : '';
          return `<pre class="zento-md-code-block"><code${langClass}>${escapeHtml(token.text)}</code></pre>`;
        },

        list: function (token) {
          const cls = token.ordered ? 'zento-md-list zento-md-ol' : 'zento-md-list zento-md-ul';
          const tag = token.ordered ? 'ol' : 'ul';
          const body = token.items.map((item) => this.listitem(item)).join('');
          return `<${tag} class="${cls}">${body}</${tag}>`;
        },

        listitem: function (token) {
          return `<li>${this.parser.parse(token.tokens)}</li>`;
        },

        hr: () => `<hr class="zento-md-hr" />`,
        br: () => `<br />`,

        link: function (token) {
          // In-widget ticket links: rendered as a clickable chip that opens the
          // ticket PDF (same view as the Tickets tab) instead of navigating away.
          const ticketMatch = /^zento-ticket:(.+)$/i.exec((token.href || '').trim());
          if (ticketMatch) {
            const ticketId = ticketMatch[1].trim();
            const linkText = this.parser.parseInline(token.tokens);
            return `<a class="zento-message-ticket-link" role="button" tabindex="0" data-ticket-id="${escapeHtml(ticketId)}"><span class="zento-message-ticket-link-icon">${icons.file}</span><span class="zento-message-ticket-link-text">${linkText}</span></a>`;
          }
          const safeHref = sanitizeHref(token.href);
          const text = this.parser.parseInline(token.tokens);
          if (!safeHref) return text; // drop unsafe links (e.g., javascript:)
          const safeTitle = token.title ? ` title="${escapeHtml(token.title)}"` : '';
          return `<a class="zento-md-link" target="_blank" rel="noopener noreferrer"${safeTitle} href="${escapeHtml(safeHref)}">${text}</a>`;
        },

        table: function (token) {
          const renderCell = (cell: {
            header: boolean;
            align: 'center' | 'left' | 'right' | null;
            tokens: Token[];
          }) => {
            const tag = cell.header ? 'th' : 'td';
            const cls = cell.header ? 'zento-md-th' : 'zento-md-td';
            const align = cell.align ? ` style="text-align:${cell.align}"` : '';
            const inner = this.parser.parseInline(cell.tokens);
            return `<${tag} class="${cls}"${align}>${inner}</${tag}>`;
          };

          const thead = `<tr>${token.header.map(renderCell).join('')}</tr>`;
          const tbody = token.rows
            .map((row) => `<tr>${row.map(renderCell).join('')}</tr>`)
            .join('');

          return `<div class="zento-md-table-wrapper"><table class="zento-md-table"><thead>${thead}</thead><tbody>${tbody}</tbody></table></div>`;
        },
      },
    });
  }

  /**
   * Initialize and mount the widget to the DOM
   */
  async init(): Promise<void> {
    try {
      await this.resolveWidgetIdentityIfConfigured();
      // Fetch assistant info
      this.state.assistantInfo = await this.apiClient.getAssistantInfo();
      this.state.isLoading = false;

      // Create widget DOM (includes Shadow DOM and style injection)
      this.createWidget();

      // Don't load existing messages - show conversation list instead
      // The user will select a conversation or start a new one

      // Start session-level subscription for global notifications
      // This allows the widget to receive messages even when closed or viewing other conversations
      this.startSessionSubscription();

      // Open if configured
      if (this.config.initiallyOpen) {
        this.open();
      }

      void this.loadWidgetUserFromApi();
      void this.refreshWidgetClientLink();

      console.log('[ZentoWidget] Initialized successfully');
    } catch (error) {
      console.error('[ZentoWidget] Failed to initialize:', error);
      this.state.isLoading = false;
      this.state.error = 'Failed to load assistant';
      this.config.onError(error as Error);

      // Still create widget but show error state
      this.createWidget();
    }
  }

  private ssoCacheKey(): string {
    return `zento_widget_sso_${this.config.apiKey.substring(0, 8)}`;
  }

  private fingerprintToken(token: string): string {
    let h = 0;
    for (let i = 0; i < token.length; i++) {
      h = (Math.imul(31, h) + token.charCodeAt(i)) | 0;
    }
    return String(h);
  }

  private async resolveWidgetIdentityIfConfigured(): Promise<void> {
    const raw = this.config.identityToken;
    if (!raw) return;

    let token: string | null;
    try {
      token = typeof raw === 'function' ? await raw() : raw;
    } catch (e) {
      console.error('[ZentoWidget] identityToken resolver failed:', e);
      this.config.onError(e instanceof Error ? e : new Error(String(e)));
      return;
    }

    if (!token || typeof token !== 'string') return;

    const fp = this.fingerprintToken(token);
    try {
      const rawCache = localStorage.getItem(this.ssoCacheKey());
      if (rawCache) {
        const cached = JSON.parse(rawCache) as {
          identity_fp: string;
          session_id: string;
          widget_user?: { name?: string; email?: string };
        };
        if (cached.identity_fp === fp && cached.session_id) {
          this.sessionId = cached.session_id;
          persistSessionIdForApiKey(this.config.apiKey, cached.session_id);
          if (cached.widget_user?.email) {
            this.state.widgetUser = {
              name: cached.widget_user.name,
              email: cached.widget_user.email,
            };
            this.saveWidgetUser(this.state.widgetUser);
          }
          return;
        }
      }
    } catch {
      /* ignore cache */
    }

    try {
      const res = await this.apiClient.exchangeWidgetIdentity(token);
      this.sessionId = res.session_id;
      persistSessionIdForApiKey(this.config.apiKey, res.session_id);
      const wu = res.widget_user;
      this.state.widgetUser = {
        name: wu.name ?? undefined,
        email: wu.email ?? undefined,
      };
      this.saveWidgetUser(this.state.widgetUser);
      localStorage.setItem(
        this.ssoCacheKey(),
        JSON.stringify({
          identity_fp: fp,
          session_id: res.session_id,
          widget_user: this.state.widgetUser,
        })
      );
    } catch (e) {
      console.error('[ZentoWidget] Widget identity exchange failed:', e);
      this.config.onError(e instanceof Error ? e : new Error(String(e)));
    }
  }

  /**
   * Inject CSS styles into the shadow DOM
   */
  private injectStyles(): void {
    if (this.styleElement || !this.shadowRoot) return;

    this.styleElement = document.createElement('style');
    this.styleElement.id = 'zento-widget-styles';
    this.styleElement.textContent = createStyles(
      this.state.assistantInfo?.primary_color || '#6366f1'
    );
    this.shadowRoot.appendChild(this.styleElement);
  }

  /**
   * Create the widget DOM structure using Shadow DOM for style isolation
   */
  private createWidget(): void {
    // Remove existing widget if present
    this.destroy();

    // Create host container (this goes in the main DOM)
    this.container = document.createElement('div');
    this.container.id = 'zento-widget-host';
    this.container.style.cssText = `
      position: fixed !important;
      z-index: ${this.config.zIndex} !important;
      ${this.config.position === 'bottom-right' ? 'bottom: 20px !important; right: 20px !important;' : 'bottom: 20px !important; left: 20px !important;'}
      font-size: 16px !important;
      line-height: 1.5 !important;
    `;

    // Create Shadow DOM for complete style isolation
    this.shadowRoot = this.container.attachShadow({ mode: 'open' });

    // Inject styles into shadow DOM
    this.injectStyles();

    // Create widget root inside shadow DOM
    this.widgetRoot = document.createElement('div');
    this.widgetRoot.className = `zento-widget-container zento-position-${this.config.position}`;
    this.shadowRoot.appendChild(this.widgetRoot);

    // Create floating button
    const button = this.createFloatingButton();
    this.widgetRoot.appendChild(button);

    // Create chat window
    this.chatWindow = this.createChatWindow();
    this.widgetRoot.appendChild(this.chatWindow);

    // Mount to body
    document.body.appendChild(this.container);

    // Update state
    if (this.state.isOpen) {
      this.updateUIState();
    }
  }

  /**
   * Create the floating action button
   */
  private createFloatingButton(): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'zento-floating-button';
    button.setAttribute('aria-label', 'Open chat');

    // Use custom bubble icon if provided
    const bubbleIconUrl = this.state.assistantInfo?.bubble_icon_url;
    const chatIconHtml = bubbleIconUrl
      ? `<img src="${this.escapeHtml(bubbleIconUrl)}" alt="" class="zento-custom-bubble-icon" />`
      : icons.chat;

    button.innerHTML = `
      <span class="zento-chat-icon">${chatIconHtml}</span>
      <span class="zento-close-icon">${icons.close}</span>
      <span class="zento-unread-badge" style="display: none;"></span>
    `;

    button.addEventListener('click', () => this.toggle());

    return button;
  }

  /**
   * Update the unread badge on the floating button
   */
  private updateUnreadBadge(): void {
    const badge = this.widgetRoot?.querySelector('.zento-unread-badge') as HTMLElement;
    if (!badge) return;

    const count = this.state.totalUnreadCount;
    if (count > 0 && !this.state.isOpen) {
      badge.textContent = count > 99 ? '99+' : String(count);
      badge.style.display = 'flex';
    } else {
      badge.textContent = '';
      badge.style.display = 'none';
    }
  }

  /**
   * True when the user is looking at this conversation's thread (not the list, not another chat).
   * Used for read receipts and to avoid counting notifications while the thread is visible.
   */
  private isUserActivelyViewingConversation(conversationId: string): boolean {
    return (
      this.state.isOpen &&
      this.state.conversationId === conversationId &&
      !this.state.showConversationList
    );
  }

  /**
   * Session and conversation channels can both receive the same broadcast; consume once per message.
   */
  private tryConsumeRealtimeNotificationDedupe(conversationId: string, messageId: string): boolean {
    const key = `${conversationId}:${messageId}`;
    if (this.recentNotificationDedupeKeys.includes(key)) {
      return false;
    }
    this.recentNotificationDedupeKeys.push(key);
    if (this.recentNotificationDedupeKeys.length > 150) {
      this.recentNotificationDedupeKeys.splice(0, this.recentNotificationDedupeKeys.length - 150);
    }
    return true;
  }

  /**
   * Reconcile badge with server (fixes drift from duplicate events or missed mark-read).
   */
  private async syncUnreadCountFromServer(): Promise<void> {
    try {
      const response = await this.apiClient.listConversations(this.sessionId, {
        limit: WIDGET_CONVERSATIONS_PAGE_SIZE,
        offset: 0,
      });
      this.state.totalUnreadCount = response.total_unread ?? 0;
      if (Array.isArray(response.conversations)) {
        this.state.conversations = response.conversations;
      }
      this.conversationsOffset = response.conversations.length;
      this.conversationsHasMore = response.has_more ?? false;
      this.updateUnreadBadge();
      if (this.state.showConversationList && this.conversationListPanel) {
        this.renderConversationList();
        this.wireConversationListLoadMore();
      }
    } catch {
      /* ignore */
    }
  }

  /** Plain-text snippet for list previews and toasts (never show raw HTML). */
  private toPlainTextPreview(content: string, maxLen = 60): string {
    if (!content?.trim()) return '';
    const raw = stripWidgetHtmlPrefix(content);
    const sanitized = DOMPurify.sanitize(raw, { USE_PROFILES: { html: true } });
    const tmp = document.createElement('div');
    tmp.innerHTML = sanitized;
    const text = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
    if (!text) return '';
    return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
  }

  /**
   * Show a toast notification for a new message
   */
  private showNewMessageToast(content: string, conversationId: string): void {
    // Remove any existing toast
    const existingToast = this.widgetRoot?.querySelector('.zento-toast');
    if (existingToast) {
      existingToast.remove();
    }

    // Truncate content (avoid empty toast body when only attachments / whitespace)
    const previewText = this.toPlainTextPreview(content, 100);
    const truncatedContent = previewText || 'Nuevo mensaje';

    // Build avatar HTML
    const logoUrl = this.state.assistantInfo?.logo_url;
    const avatarLetter = 'S';
    const avatarHtml = logoUrl
      ? `<img src="${this.escapeHtml(logoUrl)}" alt="" class="zento-toast-avatar-img" />`
      : `<span class="zento-toast-avatar-letter">${avatarLetter}</span>`;

    // Create toast element
    const toast = document.createElement('div');
    toast.className = 'zento-toast';
    toast.innerHTML = `
      <div class="zento-toast-avatar">
        ${avatarHtml}
      </div>
      <div class="zento-toast-body">
        <div class="zento-toast-header">
          <span class="zento-toast-sender">Nuevo mensaje</span>
          <span class="zento-toast-time">Ahora</span>
        </div>
        <div class="zento-toast-content">${this.escapeHtml(truncatedContent)}</div>
      </div>
      <div class="zento-toast-arrow">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </div>
      <button class="zento-toast-close" aria-label="Cerrar">${icons.x}</button>
    `;

    // Add event listeners
    const closeBtn = toast.querySelector('.zento-toast-close');
    closeBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      toast.classList.add('zento-toast-hiding');
      setTimeout(() => toast.remove(), 200);
    });

    // Clicking anywhere on toast opens the conversation (await open when identityToken is async)
    toast.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.zento-toast-close')) return;
      toast.remove();
      void this.openAndSwitchToConversation(conversationId);
    });

    // Add to widget root
    this.widgetRoot?.appendChild(toast);

    // Auto-dismiss after 8 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.classList.add('zento-toast-hiding');
        setTimeout(() => toast.remove(), 200);
      }
    }, 8000);
  }

  /**
   * Create the chat window
   */
  private createChatWindow(): HTMLDivElement {
    const window = document.createElement('div');
    window.className = 'zento-chat-window';

    const info = this.state.assistantInfo;
    const assistantName = 'Soporte';
    const avatarLetter = 'S';

    // Use custom logo if provided, otherwise show avatar with letter
    const logoUrl = info?.logo_url;
    const avatarHtml = logoUrl
      ? `<img src="${this.escapeHtml(logoUrl)}" alt="" class="zento-header-logo" />`
      : `<div class="zento-header-avatar"><span class="zento-header-avatar-letter">${avatarLetter}</span></div>`;

    window.innerHTML = `
      <div class="zento-header">
        <button class="zento-menu-button" aria-label="Ver conversaciones">
          ${icons.back}
        </button>
        ${avatarHtml}
        <div class="zento-header-info">
          <h3 class="zento-header-title">${this.escapeHtml(assistantName)}</h3>
          <div class="zento-header-status">
            <span class="zento-status-dot"></span>
            En línea
          </div>
        </div>
        <button type="button" class="zento-human-request-button" aria-label="Hablar con un humano">
          ${icons.headset}
          <span class="zento-human-request-text">Humano</span>
        </button>
        <button class="zento-close-button" aria-label="Cerrar chat">
          ${icons.x}
        </button>
      </div>
      <div class="zento-conversation-list" style="display: ${this.state.showConversationList ? 'flex' : 'none'};">
        <div class="zento-conversation-list-header">
          <button class="zento-new-chat-button" aria-label="Nueva conversación">
            ${icons.plus}
          </button>
          <span class="zento-conversation-list-title">Mensajes</span>
          <button class="zento-conversation-close-btn" aria-label="Cerrar">
            ${icons.x}
          </button>
        </div>
        <div class="zento-inbox-chats-toolbar">
          <button type="button" class="zento-conversation-new-primary" aria-label="Nueva conversación">
            ${icons.plus}
            <span class="zento-conversation-new-primary-label">Nueva conversación</span>
          </button>
        </div>
        <div class="zento-conversation-items">
          ${this.conversationListSkeletonHtml()}
        </div>
        <div class="zento-ticket-dev-usage zento-inbox-panel-hidden" hidden></div>
        <div class="zento-ticket-items zento-inbox-panel-hidden">
          ${this.conversationListSkeletonHtml(6, { ariaLabel: 'Cargando tickets' })}
        </div>
        <div class="zento-inbox-tabs zento-inbox-panel-hidden" hidden>
          <button type="button" class="zento-inbox-tab zento-inbox-tab-active" data-inbox-tab="chats" aria-label="Conversaciones">
            Chats
          </button>
          <button type="button" class="zento-inbox-tab" data-inbox-tab="tickets" aria-label="Tickets">
            Tickets
          </button>
        </div>
      </div>
      <div class="zento-messages-wrap">
        <div class="zento-messages"></div>
        <button type="button" class="zento-jump-to-bottom" aria-label="Ir al final" hidden>
          <span class="zento-jump-to-bottom-icon">${icons.chevronDown}</span>
          <span class="zento-jump-to-bottom-label">Ir al final</span>
        </button>
      </div>
      <div class="zento-input-container" style="display: ${this.state.showConversationList ? 'none' : 'block'}">
        <div class="zento-attachment-preview" style="display: none;"></div>
        <div class="zento-input-wrapper">
          <input 
            type="file" 
            class="zento-file-input" 
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint,application/zip,application/x-zip-compressed,text/plain,text/csv,.xlsx,.xls,.docx,.doc,.pptx,.ppt,.zip,.txt,.csv"
            multiple
            style="display: none;"
          />
          <button class="zento-attach-button" aria-label="Attach file" title="Adjuntar archivo (imágenes, PDF, Office, ZIP, texto)">
            ${icons.paperclip}
          </button>
          <textarea 
            class="zento-input" 
            placeholder="Escribe tu mensaje..."
            rows="1"
            aria-label="Message input"
          ></textarea>
          <button class="zento-send-button" aria-label="Send message" disabled>
            ${icons.send}
          </button>
        </div>
      </div>
    `;

    // Get references
    this.messagesContainer = window.querySelector('.zento-messages') as HTMLDivElement;
    this.jumpToBottomButton = window.querySelector('.zento-jump-to-bottom') as HTMLButtonElement;
    this.messagesContainer?.addEventListener('scroll', () => this.handleMessagesScroll());
    this.messagesContainer?.addEventListener('click', (event) => this.handleMessageContainerClick(event));
    this.messagesContainer?.addEventListener('keydown', (event) => this.handleMessageContainerKeydown(event as KeyboardEvent));
    this.jumpToBottomButton?.addEventListener('click', () => {
      this.scrollToBottom();
    });
    if (this.messagesContainer) {
      this.jumpToBottomResizeObserver = new ResizeObserver(() => {
        this.updateJumpToBottomVisibility();
      });
      this.jumpToBottomResizeObserver.observe(this.messagesContainer);
    }
    this.inputElement = window.querySelector('.zento-input');
    this.inputContainer = window.querySelector('.zento-input-container');
    this.humanRequestButton = window.querySelector('.zento-human-request-button') as HTMLButtonElement;
    this.humanRequestText = window.querySelector('.zento-human-request-text') as HTMLSpanElement;
    this.conversationListPanel = window.querySelector('.zento-conversation-list');
    this.conversationItemsContainer = window.querySelector('.zento-conversation-items');
    this.ticketItemsContainer = window.querySelector('.zento-ticket-items');
    this.ticketDevUsageContainer = window.querySelector('.zento-ticket-dev-usage');
    this.inboxTabsContainer = window.querySelector('.zento-inbox-tabs');
    this.inboxChatsToolbar = window.querySelector('.zento-inbox-chats-toolbar');
    this.fileInputElement = window.querySelector('.zento-file-input');
    this.attachmentPreviewContainer = window.querySelector('.zento-attachment-preview');
    const sendButton = window.querySelector('.zento-send-button') as HTMLButtonElement;
    const closeButton = window.querySelector('.zento-close-button') as HTMLButtonElement;
    const menuButton = window.querySelector('.zento-menu-button') as HTMLButtonElement;
    const backButton = window.querySelector('.zento-back-button') as HTMLButtonElement;
    const newChatButton = window.querySelector('.zento-new-chat-button') as HTMLButtonElement;
    const newConversationPrimaryBtn = window.querySelector(
      '.zento-conversation-new-primary',
    ) as HTMLButtonElement;
    const conversationCloseBtn = window.querySelector('.zento-conversation-close-btn') as HTMLButtonElement;
    const attachButton = window.querySelector('.zento-attach-button') as HTMLButtonElement;

    // If conversation list should be shown initially, load conversations
    if (this.state.showConversationList) {
      this.loadConversations();
    }

    // Show welcome message (no messages initially since we start with list)
    this.showWelcomeMessage();

    // Event listeners
    closeButton?.addEventListener('click', () => this.close());
    menuButton?.addEventListener('click', () => this.toggleConversationList());
    backButton?.addEventListener('click', () => this.hideConversationList());
    newChatButton?.addEventListener('click', () => this.startNewConversation());
    newConversationPrimaryBtn?.addEventListener('click', () => this.startNewConversation());
    conversationCloseBtn?.addEventListener('click', () => this.close());
    attachButton?.addEventListener('click', () => this.fileInputElement?.click());
    this.humanRequestButton?.addEventListener('click', () => {
      void this.handleHumanRequestClick();
    });

    // File input change handler
    this.fileInputElement?.addEventListener('change', (e) => this.handleFileSelect(e));

    // Handle paste events for images from clipboard (like ChatGPT)
    this.inputElement?.addEventListener('paste', (e) => this.handlePaste(e));

    this.inputElement?.addEventListener('input', () => {
      this.autoResizeInput();
      this.updateSendButtonState(sendButton);
    });

    this.inputElement?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    });

    // Mobile keyboard handling
    this.inputElement?.addEventListener('focus', () => {
      this.handleMobileKeyboardOpen();
    });

    this.inputElement?.addEventListener('blur', () => {
      this.handleMobileKeyboardClose();
    });

    sendButton?.addEventListener('click', () => this.sendMessage());

    this.inboxTabsContainer?.querySelectorAll('.zento-inbox-tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tab = btn.getAttribute('data-inbox-tab') as WidgetInboxTab | null;
        if (tab === 'chats' || tab === 'tickets') {
          void this.setInboxTab(tab);
        }
      });
    });

    // Set initial state for human request controls
    this.updateHumanRequestBar();
    this.updateInboxTabsVisibility();
    this.updateInboxPanelVisibility();

    // Listen for page visibility changes (user returns from background/other tab)
    document.addEventListener('visibilitychange', () => this.handleVisibilityChange());

    return window;
  }

  /**
   * Show welcome message in the chat
   */
  private showWelcomeMessage(): void {
    if (!this.messagesContainer || !this.state.assistantInfo) return;

    const welcomeText = this.config.customGreeting ||
      'Hola, ¿en qué podemos ayudarte hoy?';

    // Use custom logo if provided, otherwise use sparkle icon
    const logoUrl = this.state.assistantInfo.logo_url;
    const avatarLetter = 'S';
    const welcomeIconHtml = logoUrl
      ? `<img src="${this.escapeHtml(logoUrl)}" alt="" class="zento-welcome-logo" />`
      : `<span class="zento-welcome-avatar-letter">${avatarLetter}</span>`;

    this.messagesContainer.innerHTML = `
      <div class="zento-welcome">
        <div class="zento-welcome-icon">
          ${welcomeIconHtml}
        </div>
        <h4 class="zento-welcome-title">¡Hola! 👋</h4>
        <p class="zento-welcome-text">${this.escapeHtml(welcomeText)}</p>
      </div>
    `;
  }

  /**
   * Render messages in the chat window
   */
  private renderMessages(options?: {
    /** Keep viewport anchored after prepending older messages (not only when scrollTop === 0). */
    preserveScrollAfterPrepend?: { scrollHeight: number; scrollTop: number };
  }): void {
    if (!this.messagesContainer) return;

    this.messagesContainer.innerHTML = '';

    if (this.messagesHasMore) {
      const topBar = document.createElement('div');
      topBar.className = 'zento-messages-load-more';
      if (this.isLoadingMoreMessages) {
        topBar.innerHTML = this.messagesLoadMoreSkeletonHtml();
      } else {
        topBar.innerHTML = `
          <button type="button" class="zento-messages-load-more-btn">
            Cargar mensajes anteriores
          </button>`;
        topBar.querySelector('.zento-messages-load-more-btn')?.addEventListener('click', () => {
          void this.loadMoreOlderMessages();
        });
      }
      this.messagesContainer.appendChild(topBar);
    }

    for (const message of this.state.messages) {
      this.appendMessage(message, false, true);
    }

    const preserve = options?.preserveScrollAfterPrepend;
    if (preserve) {
      const prevH = preserve.scrollHeight;
      const prevT = preserve.scrollTop;
      // Two rAFs: layout after innerHTML + DOM from appendMessage.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = this.messagesContainer;
          if (!el) return;
          const newH = el.scrollHeight;
          const delta = newH - prevH;
          // Avoid smooth scroll animating this jump (feels like “scroll down”).
          el.style.setProperty('scroll-behavior', 'auto', 'important');
          el.scrollTop = prevT + delta;
          el.style.removeProperty('scroll-behavior');
          this.updateJumpToBottomVisibility();
        });
      });
    } else {
      this.scrollToBottom();
    }
  }

  /**
   * Append a message to the chat
   */
  private appendMessage(message: MessageWithDebug, animate: boolean = true, skipScroll = false): void {
    if (!this.messagesContainer) return;

    // Remove welcome message if present
    const welcome = this.messagesContainer.querySelector('.zento-welcome');
    if (welcome) {
      welcome.remove();
    }

    // Determine CSS class based on role
    const roleClass = message.role === 'human_agent' ? 'zento-human-agent' : `zento-${message.role}`;

    const messageEl = document.createElement('div');
    messageEl.className = `zento-message ${roleClass}`;
    messageEl.setAttribute('data-zento-message-id', message.id);
    if (!animate) {
      messageEl.style.animation = 'none';
    }

    const time = message.created_at
      ? this.formatTime(new Date(message.created_at))
      : this.formatTime(new Date());

    // Build debug panel HTML if debug info exists and showDebug is enabled
    let debugHtml = '';
    if (this.config.showDebug && message.debug && message.role === 'assistant') {
      const debug = message.debug;
      const filesCount = debug.files_details?.length || 0;
      const totalTime = debug.retrieval_time_ms + debug.llm_time_ms;

      debugHtml = `
        <div class="zento-debug-toggle">
          <button class="zento-debug-btn" aria-label="Toggle debug info">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
            </svg>
            ${filesCount} archivos • ${totalTime}ms
          </button>
        </div>
        <div class="zento-debug-panel" style="display: none;">
          <div class="zento-debug-header">
            <span class="zento-debug-title">🔍 Debug Info</span>
            <button class="zento-debug-close" aria-label="Close debug">&times;</button>
          </div>
          <div class="zento-debug-content">
            ${this.renderDebugTiming(debug)}
            ${this.renderDebugLayers(debug)}
            ${this.renderDebugFiles(debug)}
          </div>
        </div>
      `;
    }

    // Build attachment previews for user and human_agent messages
    let attachmentHtml = '';
    if ((message.role === 'user' || message.role === 'human_agent') && message.attachments && message.attachments.length > 0) {
      attachmentHtml = `
        <div class="zento-message-attachments">
          ${message.attachments.map((att) => {
        if (att.type === 'image') {
          // Priority: public_url (from backend) > data URL (from local upload)
          const imgSrc = att.public_url || (att.data ? `data:${att.media_type};base64,${att.data}` : '');
          if (!imgSrc) return ''; // Skip if no valid source
          // Make image clickable to open in new tab
          return `<a href="${imgSrc}" target="_blank" rel="noopener noreferrer" class="zento-message-attachment-link">
            <img src="${imgSrc}" alt="${this.escapeHtml(att.filename || 'Image')}" class="zento-message-attachment-img" />
          </a>`;
        } else {
          // Make document/file clickable to open/download
          // Priority: public_url (from backend) > data URL (from local upload)
          const fileUrl = att.public_url || (att.data ? `data:${att.media_type};base64,${att.data}` : '');
          const filename = att.filename || 'Archivo';

          if (!fileUrl) {
            // No URL available - show non-clickable placeholder
            return `
                <div class="zento-message-attachment-file zento-attachment-pending">
                  ${icons.file}
                  <span>${this.escapeHtml(filename)}</span>
                </div>
              `;
          }

          // For data URLs, use download attribute to enable file download
          const isDataUrl = fileUrl.startsWith('data:');
          const downloadAttr = isDataUrl ? `download="${this.escapeHtml(filename)}"` : '';

          return `
                <a href="${fileUrl}" target="_blank" rel="noopener noreferrer" ${downloadAttr} class="zento-message-attachment-file">
                  ${icons.file}
                  <span>${this.escapeHtml(filename)}</span>
                  ${icons.externalLink}
                </a>
              `;
        }
      }).join('')}
        </div>
      `;
    }

    // Use a generic label for human-agent messages to avoid exposing agent names.
    let humanAgentLabelHtml = '';
    if (message.role === 'human_agent') {
      humanAgentLabelHtml = `
        <div class="zento-human-agent-label">
          ${icons.userCheck}
          <span>Soporte</span>
        </div>
      `;
    }

    // Build avatar HTML for assistant/human_agent messages
    let avatarHtml = '';
    if (message.role === 'assistant' || message.role === 'human_agent') {
      const logoUrl = this.state.assistantInfo?.logo_url;
      const avatarLetter = 'S';

      avatarHtml = logoUrl
        ? `<div class="zento-message-avatar"><img src="${this.escapeHtml(logoUrl)}" alt="" class="zento-message-avatar-img" /></div>`
        : `<div class="zento-message-avatar"><span class="zento-message-avatar-letter">${avatarLetter}</span></div>`;
    }

    messageEl.innerHTML = `
      ${avatarHtml}
      <div class="zento-message-content">
        ${humanAgentLabelHtml}
        ${attachmentHtml}
        ${message.content ? `<div class="zento-message-bubble">${this.formatMessageContent(message.content)}</div>` : ''}
        ${debugHtml}
        <span class="zento-message-time">${time}${message.response_time_ms ? ` • ${message.response_time_ms}ms` : ''}</span>
      </div>
    `;

    // Add debug toggle event listeners
    if (this.config.showDebug && message.debug) {
      const toggleBtn = messageEl.querySelector('.zento-debug-btn');
      const debugPanel = messageEl.querySelector('.zento-debug-panel');
      const closeBtn = messageEl.querySelector('.zento-debug-close');

      toggleBtn?.addEventListener('click', () => {
        if (debugPanel) {
          const isVisible = debugPanel.getAttribute('style')?.includes('display: block');
          debugPanel.setAttribute('style', isVisible ? 'display: none;' : 'display: block;');
        }
      });

      closeBtn?.addEventListener('click', () => {
        debugPanel?.setAttribute('style', 'display: none;');
      });
    }

    this.messagesContainer.appendChild(messageEl);
    if (!skipScroll) {
      this.scrollToBottom();
    } else {
      this.updateJumpToBottomVisibility();
    }
  }

  /** Open the linked ticket when a user clicks an in-message ticket chip. */
  private handleMessageContainerClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const link = target?.closest('.zento-message-ticket-link') as HTMLElement | null;
    if (!link) return;
    event.preventDefault();
    const ticketId = link.getAttribute('data-ticket-id');
    if (ticketId) {
      void this.openTicketPdf(ticketId);
    }
  }

  /** Keyboard activation (Enter/Space) for in-message ticket chips. */
  private handleMessageContainerKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    const target = event.target as HTMLElement | null;
    const link = target?.closest('.zento-message-ticket-link') as HTMLElement | null;
    if (!link) return;
    event.preventDefault();
    const ticketId = link.getAttribute('data-ticket-id');
    if (ticketId) {
      void this.openTicketPdf(ticketId);
    }
  }

  /**
   * True when the user has scrolled near the top of the thread (wants older messages).
   * When all loaded messages fit in the viewport, scrollTop stays 0 after scrollToBottom() —
   * that must NOT be treated as "near top" or we chain-load every older page.
   */
  private isUserNearTopForOlderMessages(): boolean {
    const el = this.messagesContainer;
    if (!el) return false;
    if (el.scrollHeight <= el.clientHeight + 1) {
      return false;
    }
    return el.scrollTop < 100;
  }

  private handleMessagesScroll(): void {
    this.updateJumpToBottomVisibility();
    if (!this.messagesContainer || !this.state.conversationId) return;
    if (!this.canTriggerMessageLoadMore) return;
    if (!this.messagesHasMore || this.isLoadingMoreMessages || this.state.isTyping) return;
    if (this.isUserNearTopForOlderMessages()) {
      void this.loadMoreOlderMessages();
    }
  }

  private async loadMoreOlderMessages(): Promise<void> {
    const cid = this.state.conversationId;
    if (!cid || !this.messagesContainer || !this.messagesHasMore || this.isLoadingMoreMessages || this.state.isTyping) {
      return;
    }
    const prevScrollHeight = this.messagesContainer.scrollHeight;
    const prevScrollTop = this.messagesContainer.scrollTop;
    this.isLoadingMoreMessages = true;
    this.canTriggerMessageLoadMore = false;
    this.renderMessages({
      preserveScrollAfterPrepend: { scrollHeight: prevScrollHeight, scrollTop: prevScrollTop },
    });

    try {
      const newOffset = Math.max(0, this.messagesOffset - WIDGET_MESSAGES_PAGE_SIZE);
      const response = await this.apiClient.getConversationMessages(cid, {
        offset: newOffset,
        limit: WIDGET_MESSAGES_PAGE_SIZE,
      });
      const incoming = response.messages as MessageWithDebug[];
      const merged = [...incoming, ...this.state.messages];
      const seen = new Set<string>();
      const deduped = merged.filter((m) => {
        if (seen.has(m.id)) return false;
        seen.add(m.id);
        return true;
      });
      this.state.messages = sortChatMessagesForDisplay(deduped);
      this.messagesOffset = response.offset ?? newOffset;
      this.messagesHasMore = response.has_more ?? false;
      this.renderMessages({
        preserveScrollAfterPrepend: { scrollHeight: prevScrollHeight, scrollTop: prevScrollTop },
      });
    } catch (error) {
      console.error('[ZentoWidget] Failed to load older messages:', error);
    } finally {
      this.isLoadingMoreMessages = false;
      window.setTimeout(() => {
        this.canTriggerMessageLoadMore = true;
      }, 400);
    }
  }

  private disconnectConversationListLoadMore(): void {
    if (this.conversationListLoadMoreObserver) {
      this.conversationListLoadMoreObserver.disconnect();
      this.conversationListLoadMoreObserver = null;
    }
  }

  private disconnectTicketListLoadMore(): void {
    if (this.ticketListLoadMoreObserver) {
      this.ticketListLoadMoreObserver.disconnect();
      this.ticketListLoadMoreObserver = null;
    }
  }

  private wireConversationListLoadMore(): void {
    this.disconnectConversationListLoadMore();
    if (!this.conversationsHasMore || this.isLoadingMoreConversations) return;
    const itemsContainer = this.conversationListPanel?.querySelector('.zento-conversation-items');
    const sentinel = itemsContainer?.querySelector('.zento-conversation-load-more-sentinel');
    if (!itemsContainer || !sentinel) return;

    this.conversationListLoadMoreObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && this.conversationsHasMore && !this.isLoadingMoreConversations) {
          void this.loadConversations({ loadMore: true });
        }
      },
      { root: itemsContainer, rootMargin: '100px', threshold: 0 }
    );
    this.conversationListLoadMoreObserver.observe(sentinel);
  }

  private wireTicketListLoadMore(): void {
    this.disconnectTicketListLoadMore();
    if (!this.ticketsHasMore || this.isLoadingMoreTickets) return;
    const itemsContainer = this.ticketItemsContainer;
    const sentinel = itemsContainer?.querySelector('.zento-ticket-load-more-sentinel');
    if (!itemsContainer || !sentinel) return;

    this.ticketListLoadMoreObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && this.ticketsHasMore && !this.isLoadingMoreTickets) {
          void this.loadClientTickets({ loadMore: true });
        }
      },
      { root: itemsContainer, rootMargin: '100px', threshold: 0 },
    );
    this.ticketListLoadMoreObserver.observe(sentinel);
  }

  /**
   * Render debug timing info
   */
  private renderDebugTiming(debug: NonNullable<MessageWithDebug['debug']>): string {
    return `
      <div class="zento-debug-section">
        <div class="zento-debug-timing">
          <div class="zento-timing-item">
            <span class="zento-timing-label">Retrieval</span>
            <span class="zento-timing-value">${debug.retrieval_time_ms}ms</span>
          </div>
          <div class="zento-timing-item">
            <span class="zento-timing-label">LLM</span>
            <span class="zento-timing-value">${debug.llm_time_ms}ms</span>
          </div>
          <div class="zento-timing-item">
            <span class="zento-timing-label">Contexto</span>
            <span class="zento-timing-value">${debug.context_tokens} tokens</span>
          </div>
          ${debug.iterations_used > 1 ? `
          <div class="zento-timing-item">
            <span class="zento-timing-label">Iteraciones</span>
            <span class="zento-timing-value">${debug.iterations_used}</span>
          </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  /**
   * Render debug layers info
   */
  private renderDebugLayers(debug: NonNullable<MessageWithDebug['debug']>): string {
    if (!debug.files_by_layer || debug.files_by_layer.length === 0) return '';

    const layers = debug.files_by_layer
      .filter(l => l.files.length > 0)
      .map(layer => `
        <div class="zento-debug-layer">
          <span class="zento-layer-name">${layer.layer}</span>
          <span class="zento-layer-count">${layer.files.length}</span>
        </div>
      `).join('');

    return `
      <div class="zento-debug-section">
        <div class="zento-debug-subtitle">Archivos por capa</div>
        <div class="zento-debug-layers">${layers}</div>
      </div>
    `;
  }

  /**
   * Render debug files info
   */
  private renderDebugFiles(debug: NonNullable<MessageWithDebug['debug']>): string {
    if (!debug.files_details || debug.files_details.length === 0) return '';

    const files = debug.files_details.slice(0, 5).map(file => `
      <div class="zento-debug-file">
        <div class="zento-file-path">${this.escapeHtml(file.file_path.split('/').pop() || file.file_path)}</div>
        <div class="zento-file-meta">
          <span class="zento-file-layer">${file.layer}</span>
          <span class="zento-file-similarity">${Math.round(file.similarity * 100)}%</span>
        </div>
      </div>
    `).join('');

    const remaining = debug.files_details.length - 5;
    const moreText = remaining > 0 ? `<div class="zento-debug-more">+${remaining} más</div>` : '';

    return `
      <div class="zento-debug-section">
        <div class="zento-debug-subtitle">Archivos relevantes</div>
        <div class="zento-debug-files">${files}${moreText}</div>
      </div>
    `;
  }

  /**
   * Show typing indicator with optional status message
   */
  private showTypingIndicator(statusMessage?: string): void {
    if (!this.messagesContainer) return;

    let indicator = this.messagesContainer.querySelector('.zento-typing-indicator') as HTMLElement;

    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'zento-typing-indicator';
      this.messagesContainer.appendChild(indicator);
    }

    // Update content with dots and optional message
    indicator.innerHTML = `
      <div class="zento-typing-dots">
        <span class="zento-typing-dot"></span>
        <span class="zento-typing-dot"></span>
        <span class="zento-typing-dot"></span>
      </div>
      ${statusMessage ? `<span class="zento-typing-status">${statusMessage}</span>` : ''}
    `;
    this.updateJumpToBottomVisibility();
  }

  /**
   * Update the typing indicator with a new status message
   */
  private updateTypingStatus(statusMessage: string): void {
    if (!this.messagesContainer) return;

    const indicator = this.messagesContainer.querySelector('.zento-typing-indicator');
    if (indicator) {
      // Just update the status text, keeping the dots
      let statusEl = indicator.querySelector('.zento-typing-status');
      if (!statusEl) {
        statusEl = document.createElement('span');
        statusEl.className = 'zento-typing-status';
        indicator.appendChild(statusEl);
      }
      statusEl.textContent = statusMessage;
    } else {
      // If no indicator exists, show one with the message
      this.showTypingIndicator(statusMessage);
    }
    this.updateJumpToBottomVisibility();
  }

  /**
   * Hide typing indicator
   */
  private hideTypingIndicator(): void {
    if (!this.messagesContainer) return;

    const indicator = this.messagesContainer.querySelector('.zento-typing-indicator');
    if (indicator) {
      indicator.remove();
    }
    this.updateJumpToBottomVisibility();
  }

  /**
   * Remove a pending user message from state and DOM (e.g. after stream failure).
   */
  private removeMessageById(messageId: string): void {
    const idx = this.state.messages.findIndex((m) => m.id === messageId);
    if (idx !== -1) {
      this.state.messages.splice(idx, 1);
    }
    const sel = `[data-zento-message-id="${CSS.escape(messageId)}"]`;
    this.messagesContainer?.querySelector(sel)?.remove();
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    if (!this.messagesContainer) return;

    // Remove existing error
    const existing = this.messagesContainer.querySelector('.zento-error');
    if (existing) existing.remove();

    const errorEl = document.createElement('div');
    errorEl.className = 'zento-error';
    errorEl.innerHTML = `
      <span class="zento-error-icon">${icons.error}</span>
      <span class="zento-error-text">${this.escapeHtml(message)}</span>
      <button class="zento-error-dismiss" aria-label="Dismiss error">
        ${icons.close}
      </button>
    `;

    errorEl.querySelector('.zento-error-dismiss')?.addEventListener('click', () => {
      errorEl.remove();
    });

    this.messagesContainer.appendChild(errorEl);
    this.scrollToBottom();
  }

  /**
   * Send a message to the API using streaming to avoid timeout issues
   */
  private async sendMessage(): Promise<void> {
    if (!this.inputElement || this.state.isTyping) return;

    const message = this.inputElement.value.trim();
    const currentAttachments = [...this.state.attachments];

    // Need either text or attachments
    if (!message && currentAttachments.length === 0) return;

    // Clear input and attachments
    this.inputElement.value = '';
    this.autoResizeInput();
    this.clearAttachments();
    const sendButton = this.widgetRoot?.querySelector('.zento-send-button') as HTMLButtonElement;
    if (sendButton) sendButton.disabled = true;

    // Create message attachments WITHOUT preview_url (which gets revoked)
    // Use only the data needed to display via data URL
    const messageAttachments: WidgetAttachment[] = currentAttachments.map((a) => {
      const rest = { ...a };
      delete rest.preview_url;
      return rest;
    });

    // Add user message with attachments
    const userMessage: MessageWithDebug = {
      id: `temp_${Date.now()}`,
      role: 'user',
      content: message,
      attachments: messageAttachments.length > 0 ? messageAttachments : undefined,
    };
    const pendingUserMessageId = userMessage.id;
    this.state.messages.push(userMessage);
    this.appendMessage(userMessage);

    // Callback
    this.config.onMessageSent(message);

    // Show typing
    this.state.isTyping = true;
    this.showTypingIndicator();

    // Create a placeholder for streaming message
    let streamingMessageEl: HTMLDivElement | null = null as HTMLDivElement | null;
    let streamingBubble: HTMLDivElement | null = null as HTMLDivElement | null;
    let fullContent = '';

    // Capture and reset forceNewConversation flag
    // Also force new if there's no conversationId to avoid reusing old conversations by session_id
    const shouldForceNew = this.state.forceNewConversation || this.state.conversationId === null;
    this.state.forceNewConversation = false;

    // Prepare attachments for API (remove preview_url)
    const apiAttachments: FileAttachment[] | undefined = currentAttachments.length > 0
      ? currentAttachments.map((a) => {
        const rest = { ...a };
        delete rest.preview_url;
        return rest as FileAttachment;
      })
      : undefined;

    try {
      await this.apiClient.sendMessageStream(
        this.sessionId,
        message,
        {
          onThinking: () => {
            // Keep showing typing indicator
          },

          onKeepalive: (statusMessage: string) => {
            // Update typing indicator with status message
            this.updateTypingStatus(statusMessage);
          },

          onContentReset: () => {
            fullContent = '';

            if (streamingBubble) {
              streamingBubble.innerHTML = '';
            }

            if (streamingMessageEl) {
              streamingMessageEl.remove();
            }

            streamingMessageEl = null;
            streamingBubble = null;
          },

          onTextDelta: (_chunk: string, fullSoFar: string) => {
            // Create streaming message element if not exists
            if (!streamingMessageEl && this.messagesContainer) {
              this.hideTypingIndicator();

              // Build avatar HTML
              const logoUrl = this.state.assistantInfo?.logo_url;
              const avatarLetter = 'S';
              const avatarHtml = logoUrl
                ? `<div class="zento-message-avatar"><img src="${this.escapeHtml(logoUrl)}" alt="" class="zento-message-avatar-img" /></div>`
                : `<div class="zento-message-avatar"><span class="zento-message-avatar-letter">${avatarLetter}</span></div>`;

              // Create message element
              streamingMessageEl = document.createElement('div');
              streamingMessageEl.className = 'zento-message zento-assistant';
              streamingMessageEl.innerHTML = `
                ${avatarHtml}
                <div class="zento-message-content">
                  <div class="zento-message-bubble"></div>
                  <span class="zento-message-time">${this.formatTime(new Date())}</span>
                </div>
              `;
              streamingBubble = streamingMessageEl.querySelector('.zento-message-bubble');
              this.messagesContainer.appendChild(streamingMessageEl);
            }

            // Update content with streaming text
            fullContent = fullSoFar;
            if (streamingBubble) {
              streamingBubble.innerHTML = this.formatMessageContent(fullContent);
            }
            this.updateJumpToBottomVisibility();
          },

          onComplete: (response) => {
            // Hide typing indicator (in case no text_delta was received)
            this.hideTypingIndicator();
            this.state.isTyping = false;

            const completedConversationId = response.conversation_id;
            const displayContent = response.content;
            const completedMessageId = String(response.message_id || '');
            // Snapshot before mutating — isUserActivelyViewingConversation would always match
            // after we assign state.conversationId = response.conversation_id
            const priorConversationId = this.state.conversationId;
            const wasListVisible = this.state.showConversationList;
            const isViewingThisStream =
              this.state.isOpen &&
              !wasListVisible &&
              (priorConversationId === completedConversationId ||
                (priorConversationId === null && !!completedConversationId));

            // Store conversation ID
            this.state.conversationId = response.conversation_id;
            storeConversationId(this.config.apiKey, response.conversation_id);
            // Keep listening to assignment changes even while the conversation is in AI mode.
            this.startMessageSubscription(response.conversation_id);
            this.updateHumanRequestBar();

            // Unread + toast when this reply finishes off-thread (deduped with session realtime)
            if (isViewingThisStream) {
              void this.markConversationAsRead(completedConversationId);
            } else if (completedMessageId) {
              if (this.tryConsumeRealtimeNotificationDedupe(completedConversationId, completedMessageId)) {
                this.state.totalUnreadCount++;
                this.updateUnreadBadge();
                this.showNewMessageToast(displayContent, completedConversationId);
              }
            } else {
              void this.syncUnreadCountFromServer();
            }

            // If the user switched chats while streaming, switchConversation() replaces
            // .zento-messages — streaming nodes are detached; updating them does nothing.
            // If they opened another conversation mid-stream, state.messages may have been
            // cleared or loaded before the user/assistant rows existed — reload from API.
            const streamStillInDom =
              !!streamingMessageEl &&
              !!streamingBubble &&
              streamingMessageEl.isConnected &&
              !!this.messagesContainer?.contains(streamingMessageEl);

            if (streamStillInDom && streamingMessageEl && streamingBubble) {
              const assistantMessage: MessageWithDebug = {
                id: response.message_id,
                role: 'assistant',
                content: displayContent,
                response_time_ms: response.response_time_ms,
              };
              this.state.messages.push(assistantMessage);
              streamingBubble.innerHTML = this.formatMessageContent(displayContent);
              const timeEl = streamingMessageEl.querySelector('.zento-message-time');
              if (timeEl) {
                timeEl.textContent = `${this.formatTime(new Date())}${response.response_time_ms ? ` • ${response.response_time_ms}ms` : ''}`;
              }
            } else {
              void this.refreshConversationMessagesFromServer(completedConversationId);
            }

            // Callback
            this.config.onMessageReceived(displayContent);
          },

          onHumanMode: (conversationId: string) => {
            // Message was sent to a human-handled conversation
            console.log('[ZentoWidget] Conversation is in human mode');

            this.hideTypingIndicator();
            this.state.isTyping = false;
            this.state.conversationId = conversationId;
            this.state.isHumanMode = true;
            this.state.humanRequested = true;
            this.state.assignedTo = null;
            storeConversationId(this.config.apiKey, conversationId);

            // Start real-time subscription to receive human agent messages
            this.startMessageSubscription(conversationId);
            this.updateHumanRequestBar();
          },

          onError: (apiError) => {
            console.error('[ZentoWidget] Stream error:', apiError);

            this.hideTypingIndicator();
            this.state.isTyping = false;

            // Remove partial streaming message if exists
            if (streamingMessageEl) {
              streamingMessageEl.remove();
            }

            this.removeMessageById(pendingUserMessageId);
            if (this.inputElement) {
              this.inputElement.value = message;
              this.autoResizeInput();
            }
            this.updateSendButtonState(sendButton);

            this.showError(this.formatSendError(apiError));
            this.config.onError(
              new Error(apiError.message || apiError.clientFacingMessage || 'Stream error'),
            );
          },
        },
        this.state.conversationId || undefined,
        shouldForceNew,
        apiAttachments,
        this.extractPageContext()
      );

      if (this.state.isTyping) {
        this.hideTypingIndicator();
        this.state.isTyping = false;
        this.updateSendButtonState(sendButton);
      }

    } catch (error: unknown) {
      console.error('[ZentoWidget] Failed to send message:', error);

      this.hideTypingIndicator();
      this.state.isTyping = false;

      // Remove partial streaming message if exists
      if (streamingMessageEl) {
        streamingMessageEl.remove();
      }

      this.removeMessageById(pendingUserMessageId);
      if (this.inputElement) {
        this.inputElement.value = message;
        this.autoResizeInput();
      }
      this.updateSendButtonState(sendButton);

      const status =
        typeof error === 'object' && error !== null && 'status' in error
          ? (error as { status?: number }).status
          : undefined;
      let clientFacingMessage: string | undefined;
      if (
        typeof error === 'object' &&
        error !== null &&
        'data' in error
      ) {
        const data = (error as { data?: unknown }).data;
        if (data && typeof data === 'object') {
          const record = data as Record<string, unknown>;
          if (typeof record.client_facing_message === 'string') {
            clientFacingMessage = record.client_facing_message;
          } else if (
            typeof record.detail === 'object' &&
            record.detail !== null &&
            typeof (record.detail as Record<string, unknown>).client_facing_message === 'string'
          ) {
            clientFacingMessage = (record.detail as { client_facing_message: string })
              .client_facing_message;
          }
        }
      }

      this.showError(
        this.formatSendError({
          clientFacingMessage,
          message: error instanceof Error ? error.message : String(error),
          status,
        }),
      );
      this.config.onError(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  }

  /**
   * Only show backend ``client_facing_message`` (or known status toasts).
   * Technical provider errors fall back to the generic copy.
   */
  private formatSendError(error: {
    clientFacingMessage?: string;
    message?: string;
    status?: number;
  }): string {
    const status = error.status;
    const technical = (error.message || '').toLowerCase();
    if (status === 429 || technical.includes('rate limit')) {
      return 'Too many messages. Please wait a moment before trying again.';
    }
    if (status === 503 || technical.includes('unavailable')) {
      return 'AI service is temporarily unavailable. Please try again later.';
    }
    const facing = (error.clientFacingMessage || '').trim();
    if (facing) {
      return facing;
    }
    return 'Failed to send message. Please try again.';
  }

  /**
   * Update send button state based on input and attachments
   */
  private updateSendButtonState(sendButton?: HTMLButtonElement): void {
    const btn = sendButton || this.widgetRoot?.querySelector('.zento-send-button') as HTMLButtonElement;
    if (btn) {
      const hasText = !!this.inputElement?.value.trim();
      const hasAttachments = this.state.attachments.length > 0;
      btn.disabled = !hasText && !hasAttachments;
    }
  }

  /**
   * Handle file selection from the file input
   */
  private async handleFileSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;

    // File types that are allowed
    const allowedTypes = [
      // Images (Claude can read these)
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      // Documents (Claude can read PDF)
      'application/pdf',
      // Microsoft Office
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'application/vnd.ms-powerpoint', // .ppt
      // Archives
      'application/zip',
      'application/x-zip-compressed',
      // Text files
      'text/plain',
      'text/csv',
      // Generic binary (some browsers report this for Office files)
      'application/octet-stream',
    ];

    // Extension to MIME type mapping (for resolving octet-stream)
    const extensionToMime: Record<string, string> = {
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.xls': 'application/vnd.ms-excel',
      '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      '.doc': 'application/msword',
      '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      '.ppt': 'application/vnd.ms-powerpoint',
      '.pdf': 'application/pdf',
      '.zip': 'application/zip',
      '.txt': 'text/plain',
      '.csv': 'text/csv',
    };

    const maxSizeMB = 20;

    for (const file of Array.from(files)) {
      // Resolve actual media type (handle application/octet-stream by checking filename)
      let actualMediaType = file.type;
      if (file.type === 'application/octet-stream' || !file.type) {
        const ext = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] || '';
        if (extensionToMime[ext]) {
          actualMediaType = extensionToMime[ext];
        }
      }

      // Validate type
      if (!allowedTypes.includes(actualMediaType) && actualMediaType !== 'application/octet-stream') {
        this.showError(`Tipo de archivo no soportado: ${file.name}`);
        continue;
      }

      // Validate size
      if (file.size > maxSizeMB * 1024 * 1024) {
        this.showError(`File too large: ${file.name} (max ${maxSizeMB}MB)`);
        continue;
      }

      // Check limit
      if (this.state.attachments.length >= 5) {
        this.showError('Maximum 5 files per message');
        break;
      }

      // Convert to base64
      try {
        const base64 = await this.fileToBase64(file);
        const isImage = actualMediaType.startsWith('image/');
        const isPdf = actualMediaType === 'application/pdf';

        // Determine file type: 'image' for images, 'document' for PDFs, 'file' for everything else
        let fileType: 'image' | 'document' | 'file' = 'file';
        if (isImage) fileType = 'image';
        else if (isPdf) fileType = 'document';

        const attachment: WidgetAttachment = {
          type: fileType,
          media_type: actualMediaType,  // Use resolved media type
          data: base64,
          filename: file.name,
          preview_url: isImage ? URL.createObjectURL(file) : undefined,
        };

        this.state.attachments.push(attachment);
      } catch (err) {
        console.error('[ZentoWidget] Error processing file:', err);
        this.showError('Error al procesar el archivo');
      }
    }

    // Reset input
    input.value = '';

    // Update UI
    this.renderAttachmentPreviews();
    this.updateSendButtonState();
  }

  /**
   * Handle paste events for images from clipboard (like ChatGPT)
   */
  private async handlePaste(event: ClipboardEvent): Promise<void> {
    const items = event.clipboardData?.items;
    if (!items) return;

    // File types that are allowed (same as handleFileSelect)
    const allowedTypes = [
      // Images (Claude can read these)
      'image/jpeg', 'image/png', 'image/gif', 'image/webp',
      // Documents (Claude can read PDF)
      'application/pdf',
      // Microsoft Office
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'application/vnd.ms-powerpoint', // .ppt
      // Archives
      'application/zip',
      'application/x-zip-compressed',
      // Text files
      'text/plain',
      'text/csv',
      // Generic binary (some browsers report this for Office files)
      'application/octet-stream',
    ];
    const maxSizeMB = 20;

    let hasProcessedFile = false;

    for (const item of Array.from(items)) {
      // Check if item is a file type we support
      if (item.kind === 'file' && (allowedTypes.includes(item.type) || item.type === '')) {
        const file = item.getAsFile();
        if (!file) continue;

        // Validate size
        if (file.size > maxSizeMB * 1024 * 1024) {
          this.showError(`Archivo muy grande: ${file.name || 'archivo'} (máx ${maxSizeMB}MB)`);
          continue;
        }

        // Check limit
        if (this.state.attachments.length >= 5) {
          this.showError('Máximo 5 archivos por mensaje');
          break;
        }

        // Convert to base64
        try {
          const base64 = await this.fileToBase64(file);

          // Resolve actual media type if needed
          const actualMediaType = file.type || 'application/octet-stream';
          const isImage = actualMediaType.startsWith('image/');
          const isPdf = actualMediaType === 'application/pdf';

          // Determine file type: 'image' for images, 'document' for PDFs, 'file' for everything else
          let fileType: 'image' | 'document' | 'file' = 'file';
          if (isImage) fileType = 'image';
          else if (isPdf) fileType = 'document';

          const attachment: WidgetAttachment = {
            type: fileType,
            media_type: actualMediaType,
            data: base64,
            filename: file.name || (isImage ? 'pasted_image.png' : 'pasted_file'),
            preview_url: isImage ? URL.createObjectURL(file) : undefined,
          };

          this.state.attachments.push(attachment);
          hasProcessedFile = true;
        } catch (err) {
          console.error('[ZentoWidget] Error processing pasted file:', err);
          this.showError('Error al procesar el archivo');
        }
      }
    }

    // Prevent default paste behavior only if we processed a file
    // This allows normal text paste to work
    if (hasProcessedFile) {
      event.preventDefault();
      // Update UI
      this.renderAttachmentPreviews();
      this.updateSendButtonState();
    }
  }

  /**
   * Convert file to base64
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data:...;base64, prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Remove an attachment by index
   */
  private removeAttachment(index: number): void {
    const removed = this.state.attachments[index];
    if (removed?.preview_url) {
      URL.revokeObjectURL(removed.preview_url);
    }
    this.state.attachments.splice(index, 1);
    this.renderAttachmentPreviews();
    this.updateSendButtonState();
  }

  /**
   * Clear all attachments
   */
  private clearAttachments(): void {
    this.state.attachments.forEach((att) => {
      if (att.preview_url) {
        URL.revokeObjectURL(att.preview_url);
      }
    });
    this.state.attachments = [];
    this.renderAttachmentPreviews();
  }

  /**
   * Render attachment previews in the input area
   */
  private renderAttachmentPreviews(): void {
    if (!this.attachmentPreviewContainer) return;

    if (this.state.attachments.length === 0) {
      this.attachmentPreviewContainer.style.display = 'none';
      this.attachmentPreviewContainer.innerHTML = '';
      return;
    }

    this.attachmentPreviewContainer.style.display = 'flex';
    this.attachmentPreviewContainer.innerHTML = this.state.attachments.map((att, idx) => {
      const isImage = att.type === 'image';
      const preview = isImage && att.preview_url
        ? `<img src="${att.preview_url}" alt="${this.escapeHtml(att.filename || 'Image')}" class="zento-attachment-thumb" />`
        : `<div class="zento-attachment-icon">${icons.file}</div>`;

      // Default filename based on type
      const defaultFilename = isImage ? 'Imagen' : 'Archivo';

      return `
        <div class="zento-attachment-item" data-index="${idx}">
          ${preview}
          <span class="zento-attachment-name">${this.escapeHtml(att.filename || defaultFilename)}</span>
          <button class="zento-attachment-remove" data-index="${idx}" aria-label="Remove">
            ${icons.x}
          </button>
        </div>
      `;
    }).join('');

    // Add remove button listeners
    const removeButtons = this.attachmentPreviewContainer.querySelectorAll('.zento-attachment-remove');
    removeButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const index = parseInt((btn as HTMLElement).dataset.index || '0', 10);
        this.removeAttachment(index);
      });
    });
  }

  /**
   * Open the chat window
   */
  open(): void {
    if (this.state.isOpen) return;

    if (this.config.identityToken) {
      void this.openWithIdentityResolve();
    } else {
      this.openImmediate();
    }
  }

  /**
   * Open the widget if closed, then load a conversation. Used by the new-message toast so
   * identity resolution finishes before switchConversation (avoids wrong scroll / load-more).
   */
  private async openAndSwitchToConversation(conversationId: string): Promise<void> {
    if (!this.state.isOpen) {
      if (this.config.identityToken) {
        await this.openWithIdentityResolve();
      } else {
        this.openImmediate();
      }
      if (!this.state.isOpen) return;
    }
    await this.switchConversation(conversationId);
  }

  /**
   * When identityToken is set, re-resolve on each open so login after page load
   * (host session changed) picks up SSO and loads the correct conversation list.
   */
  private async openWithIdentityResolve(): Promise<void> {
    if (this.state.isOpen || this.openIdentityResolveInProgress) return;
    this.openIdentityResolveInProgress = true;
    try {
      const prevSession = this.sessionId;
      await this.resolveWidgetIdentityIfConfigured();
      if (prevSession !== this.sessionId) {
        await this.applySessionIdentityChange();
      }
    } finally {
      this.openIdentityResolveInProgress = false;
    }

    if (this.state.isOpen) return;

    this.openImmediate();
  }

  /**
   * Reset local chat state and realtime bindings after session_id changes (e.g. anonymous → SSO).
   */
  private async applySessionIdentityChange(): Promise<void> {
    this.stopMessageSubscription();
    this.stopSessionSubscription();
    this.state.conversationId = null;
    this.state.messages = [];
    this.state.forceNewConversation = false;
    this.state.conversations = [];
    this.state.isHumanMode = false;
    this.state.humanRequested = false;
    this.state.assignedTo = null;
    this.state.totalUnreadCount = 0;
    this.recentNotificationDedupeKeys = [];
    const convKey = `zento_conversation_${this.config.apiKey.substring(0, 8)}`;
    try {
      localStorage.removeItem(convKey);
    } catch {
      /* ignore */
    }
    if (this.messagesContainer && this.state.assistantInfo) {
      this.showWelcomeMessage();
    }
    await this.loadWidgetUserFromApi();
    this.startSessionSubscription();
  }

  /**
   * Show the chat window (after optional identity resolve).
   */
  private openImmediate(): void {
    this.state.isOpen = true;
    this.updateUIState();

    // Hide unread badge when widget is open
    this.updateUnreadBadge();

    // Only show conversation list if:
    // - No active conversation in progress (no messages)
    // - Not currently typing/streaming a response
    // This preserves the user's context when they close and reopen the widget
    const hasActiveConversation = this.state.messages.length > 0 || this.state.isTyping;

    if (hasActiveConversation) {
      // Stay in the current chat view
      this.hideConversationList();

      // Reload messages to get any new messages sent while widget was closed
      if (this.state.conversationId) {
        // Block load-more until reload + scroll settle (stale DOM vs state caused spurious loads)
        this.canTriggerMessageLoadMore = false;
        // Mark as read while this thread is visible (WhatsApp-style), even if nothing new arrived
        void this.markConversationAsRead(this.state.conversationId);
        void this.reloadCurrentConversation({ reopen: true });
      }
    } else {
      // No active conversation, show the conversation list
      void this.showConversationList();
    }

    this.config.onOpen();
  }

  /**
   * Reload current conversation to fetch any new messages
   * Called when widget is reopened with an active conversation, or on tab visibility.
   *
   * @param reopen - After reopening the widget: always sync DOM from API. Realtime may have
   * updated state.messages while the widget was closed without touching the DOM; skipping
   * render left scrollHeight wrong and triggered infinite load-more.
   */
  private async reloadCurrentConversation(options: { reopen?: boolean } = {}): Promise<void> {
    const { reopen = false } = options;
    if (!this.state.conversationId) return;

    try {
      const response = await this.apiClient.getConversationMessages(this.state.conversationId, {
        fromEnd: true,
        limit: WIDGET_MESSAGES_PAGE_SIZE,
      });

      // Update assignment state
      const assignmentType = response.assignment_type || 'ai_agent';
      this.state.isHumanMode = assignmentType === 'human_agent';
      this.state.humanRequested = !!response.human_requested_at || assignmentType === 'human_agent';
      this.state.assignedTo = response.assigned_to || null;

      const currentMessageIds = new Set(this.state.messages.map(m => m.id));
      const newMessages = response.messages.filter(m => !currentMessageIds.has(m.id));
      if (newMessages.length > 0) {
        console.log(`[ZentoWidget] Found ${newMessages.length} new messages`);
      }

      // On reopen, always replace from server + render so DOM matches state. Otherwise only
      // when the API has ids we don't have (tab visibility: avoid resetting scroll if unchanged).
      const shouldSyncDom = reopen || newMessages.length > 0;

      if (shouldSyncDom) {
        this.canTriggerMessageLoadMore = false;
        this.state.messages = sortChatMessagesForDisplay(response.messages as MessageWithDebug[]);
        this.messagesOffset = response.offset ?? 0;
        this.messagesHasMore = response.has_more ?? false;
        this.renderMessages();
        window.setTimeout(() => {
          this.canTriggerMessageLoadMore = true;
        }, 500);
        void this.markConversationAsRead(this.state.conversationId);
      }

      // Update UI
      this.updateHumanRequestBar();

      // Always keep the active conversation subscribed so dashboard assignment
      // changes can switch the widget from AI to human mode in real time.
      this.startMessageSubscription(this.state.conversationId);

    } catch (error) {
      console.error('[ZentoWidget] Failed to reload conversation:', error);
      // Don't show error to user, keep existing messages visible
    }
  }

  /**
   * Full reload of messages after stream completes when the streaming DOM was torn down
   * (e.g. user opened the list and selected a conversation while the AI was still replying).
   */
  private async refreshConversationMessagesFromServer(conversationId: string): Promise<void> {
    try {
      const response = await this.apiClient.getConversationMessages(conversationId, {
        fromEnd: true,
        limit: WIDGET_MESSAGES_PAGE_SIZE,
      });
      const assignmentType = response.assignment_type || 'ai_agent';
      this.state.isHumanMode = assignmentType === 'human_agent';
      this.state.humanRequested = !!response.human_requested_at || assignmentType === 'human_agent';
      this.state.assignedTo = response.assigned_to || null;
      this.state.messages = sortChatMessagesForDisplay(response.messages as MessageWithDebug[]);
      this.messagesOffset = response.offset ?? 0;
      this.messagesHasMore = response.has_more ?? false;
      this.renderMessages();
      window.setTimeout(() => {
        this.canTriggerMessageLoadMore = true;
      }, 500);
      this.updateHumanRequestBar();
    } catch (error) {
      console.error('[ZentoWidget] Failed to refresh messages after stream:', error);
    }
  }

  /**
   * Handle page visibility changes.
   * Called when user returns to the page (from background, other tab, etc.)
   * Reloads messages to catch any sent while away.
   */
  private handleVisibilityChange(): void {
    // Only act when page becomes visible
    if (document.visibilityState !== 'visible') return;

    // Only reload if widget is open and has an active conversation
    if (!this.state.isOpen || !this.state.conversationId) return;

    // Don't reload if currently typing/streaming a response
    if (this.state.isTyping) return;

    console.log('[ZentoWidget] Page became visible, checking for new messages...');
    this.reloadCurrentConversation();
  }

  /**
   * Close the chat window
   */
  close(): void {
    if (!this.state.isOpen) return;

    // Add closing animation class
    this.chatWindow?.classList.add('zento-closing');

    setTimeout(() => {
      this.state.isOpen = false;
      this.updateUIState();
      this.chatWindow?.classList.remove('zento-closing');
      // Show unread badge when widget is closed
      this.updateUnreadBadge();
      void this.syncUnreadCountFromServer();
      this.config.onClose();
    }, 200);
  }

  /**
   * Toggle the chat window
   */
  toggle(): void {
    if (this.state.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Update UI to reflect current state
   */
  private updateUIState(): void {
    const button = this.widgetRoot?.querySelector('.zento-floating-button');

    if (this.state.isOpen) {
      this.chatWindow?.classList.add('zento-visible');
      button?.classList.add('zento-open');
      button?.setAttribute('aria-label', 'Close chat');
    } else {
      this.chatWindow?.classList.remove('zento-visible');
      button?.classList.remove('zento-open');
      button?.setAttribute('aria-label', 'Open chat');
    }
  }

  /**
   * Update human request button state based on conversation status
   */
  private updateHumanRequestBar(): void {
    if (!this.humanRequestButton) return;

    const hasConversation = !!this.state.conversationId;
    let label = 'Hablar con un humano';
    /** Short label next to the icon so users know what the control does */
    let visible = 'Humano';
    let disabled = false;
    let title = '';

    if (!hasConversation) {
      label = 'Inicia una conversación';
      visible = 'Humano';
      disabled = true;
      title = 'Envía un mensaje primero para poder hablar con un humano';
    } else if (this.state.assignedTo) {
      label = 'Agente conectado';
      visible = 'Agente';
      disabled = true;
      title = 'Un agente humano está atendiendo esta conversación';
    } else if (this.state.humanRequested) {
      label = 'Solicitud enviada';
      visible = 'En cola';
      disabled = true;
      title = 'Tu solicitud está en cola';
    } else {
      title = 'Pulsa para hablar con un agente humano';
    }

    const btn = this.humanRequestButton;
    btn.classList.remove(
      'zento-human-request-state-queue',
      'zento-human-request-state-active',
    );
    if (this.state.assignedTo) {
      btn.classList.add('zento-human-request-state-active');
    } else if (this.state.humanRequested) {
      btn.classList.add('zento-human-request-state-queue');
    }

    if (this.humanRequestText) {
      this.humanRequestText.textContent = visible;
    }

    btn.disabled = disabled;
    btn.setAttribute('aria-label', label);
    if (title) {
      btn.title = title;
    } else {
      btn.removeAttribute('title');
    }
  }

  /**
   * Auto-resize the input textarea
   */
  private autoResizeInput(): void {
    if (!this.inputElement) return;

    this.inputElement.style.height = 'auto';
    this.inputElement.style.height = Math.min(this.inputElement.scrollHeight, 120) + 'px';
  }

  /**
   * Check if running on mobile device
   */
  private isMobile(): boolean {
    return window.innerWidth <= 480 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  /**
   * Handle mobile keyboard opening - scroll input into view
   */
  private handleMobileKeyboardOpen(): void {
    if (!this.isMobile() || !this.chatWindow) return;

    // Add class for keyboard-open styles
    this.chatWindow.classList.add('zento-keyboard-open');

    // Use visualViewport API for better keyboard detection
    if (window.visualViewport) {
      const handleResize = () => {
        if (!this.chatWindow || !this.inputElement) return;

        // Scroll to bottom of messages when keyboard opens
        this.scrollToBottom();

        // Ensure input stays visible
        requestAnimationFrame(() => {
          this.inputElement?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        });
      };

      window.visualViewport.addEventListener('resize', handleResize, { once: true });
    } else {
      // Fallback for browsers without visualViewport
      setTimeout(() => {
        this.scrollToBottom();
        this.inputElement?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 300);
    }
  }

  /**
   * Handle mobile keyboard closing
   */
  private handleMobileKeyboardClose(): void {
    if (!this.isMobile() || !this.chatWindow) return;

    // Remove keyboard-open class
    this.chatWindow.classList.remove('zento-keyboard-open');

    // Small delay to allow keyboard animation to complete
    setTimeout(() => {
      this.scrollToBottom();
    }, 100);
  }

  /**
   * Scroll messages container to bottom
   */
  private scrollToBottom(): void {
    if (!this.messagesContainer) return;

    // Double rAF: layout after conversation list hides / widget opens (toast path) so scrollHeight is correct
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.messagesContainer) {
          this.messagesContainer.style.setProperty('scroll-behavior', 'auto', 'important');
          this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
          this.messagesContainer.style.removeProperty('scroll-behavior');
          this.updateJumpToBottomVisibility();
        }
      });
    });
  }

  /**
   * Show pill button when the user is not at the bottom (e.g. streaming without following scroll).
   */
  private updateJumpToBottomVisibility(): void {
    if (!this.jumpToBottomButton || !this.messagesContainer) return;
    if (this.state.showConversationList) {
      this.jumpToBottomButton.hidden = true;
      return;
    }
    const el = this.messagesContainer;
    const thresholdPx = 72;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom <= thresholdPx;
    this.jumpToBottomButton.hidden = atBottom;
  }

  /**
   * Format message content: ticket replies as sanitized HTML, otherwise markdown.
   */
  private formatMessageContent(content: string): string {
    if (isWidgetHtmlMessage(content)) {
      const html = stripWidgetHtmlPrefix(content);
      return this.formatHtmlMessageContent(html);
    }

    try {
      // Convert escaped newlines to real newlines (streaming sometimes sends literal \n)
      let normalizedContent = content
        .replace(/\\n/g, '\n')      // Convert literal \n to newline
        .replace(/\r\n/g, '\n')     // Normalize Windows line endings
        .replace(/\r/g, '\n');      // Normalize old Mac line endings

      // Pre-process: Insert newlines before markdown block elements that LLMs often put inline
      // This fixes cases like "text: ## Header" → "text:\n\n## Header"
      normalizedContent = this.preprocessMarkdown(normalizedContent);

      // marked.parse returns string synchronously when async is not set
      const html = marked.parse(normalizedContent, { async: false }) as string;

      return html;
    } catch (error) {
      // Fallback to escaped content if parsing fails
      console.warn('[ZentoWidget] Markdown parsing failed:', error);
      return `<p class="zento-md-p">${this.escapeHtml(content)}</p>`;
    }
  }

  /** Render ticket reply HTML (TipTap) with the same structure as the ticket view. */
  private formatHtmlMessageContent(html: string): string {
    try {
      const sanitized = sanitizeWidgetHtmlForChat(html);
      return `<div class="zento-html-content">${sanitized}</div>`;
    } catch (error) {
      console.warn('[ZentoWidget] HTML sanitization failed:', error);
      return `<p class="zento-md-p">${this.escapeHtml(html)}</p>`;
    }
  }

  /**
   * Pre-process markdown to fix common LLM formatting issues.
   * LLMs often put headers, lists, and other block elements inline without proper newlines.
   */
  private preprocessMarkdown(content: string): string {
    let result = content;

    // Insert newlines before headers that appear inline (not at start of line)
    // Match ## through ###### that aren't at the start of a line
    // Negative lookbehind for start of string or newline
    result = result.replace(/([^\n])(\s*)(#{1,6}\s+)/g, '$1\n\n$3');

    // Insert newlines before horizontal rules that appear inline
    // Match --- or *** or ___ with optional spaces
    result = result.replace(/([^\n])\s+([-*_]{3,})\s+/g, '$1\n\n$2\n\n');

    // Insert newlines before list items that appear inline after text
    // Match - or * or + or numbered lists (1. 2. etc.)
    result = result.replace(/([^\n])(\s+)([-*+]|\d+\.)\s+/g, (match, before, _space, bullet) => {
      // Don't convert if it's likely part of a sentence (e.g., "option - value" or "2.5")
      if (before.match(/[a-zA-Z0-9]$/) && bullet === '-') {
        // Check if this looks like "word - word" pattern (not a list)
        return match;
      }
      return `${before}\n${bullet} `;
    });

    // Insert newlines before blockquotes that appear inline
    result = result.replace(/([^\n])(\s*)(>\s+)/g, '$1\n\n$3');

    // Clean up excessive newlines (more than 2)
    result = result.replace(/\n{3,}/g, '\n\n');

    return result;
  }

  /**
   * Format time for display
   */
  private formatTime(date: Date): string {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Skeleton placeholders for loading states (no visible loading text).
   */
  private conversationListSkeletonHtml(
    rowCount = 6,
    options?: { compact?: boolean; ariaLabel?: string },
  ): string {
    const compactClass = options?.compact ? ' zento-conversation-skeleton-compact' : '';
    const ariaLabel = options?.ariaLabel ?? 'Cargando conversaciones';
    const rows = Array.from(
      { length: rowCount },
      () => `
      <div class="zento-skeleton-conversation-row" aria-hidden="true">
        <div class="zento-skeleton zento-skeleton-avatar"></div>
        <div class="zento-skeleton-conversation-row-text">
          <div class="zento-skeleton zento-skeleton-line zento-skeleton-line-title"></div>
          <div class="zento-skeleton zento-skeleton-line zento-skeleton-line-preview"></div>
        </div>
      </div>`,
    ).join('');
    return `<div class="zento-conversation-skeleton${compactClass}" role="status" aria-busy="true" aria-label="${ariaLabel}">${rows}</div>`;
  }

  private messagesThreadSkeletonHtml(): string {
    return `<div class="zento-messages-skeleton" role="status" aria-busy="true" aria-label="Cargando mensajes">
      <div class="zento-skeleton-bubble zento-skeleton-bubble-incoming" aria-hidden="true">
        <div class="zento-skeleton zento-skeleton-bubble-line"></div>
        <div class="zento-skeleton zento-skeleton-bubble-line zento-skeleton-bubble-line-short"></div>
      </div>
      <div class="zento-skeleton-bubble zento-skeleton-bubble-outgoing" aria-hidden="true">
        <div class="zento-skeleton zento-skeleton-bubble-line"></div>
      </div>
      <div class="zento-skeleton-bubble zento-skeleton-bubble-incoming" aria-hidden="true">
        <div class="zento-skeleton zento-skeleton-bubble-line zento-skeleton-bubble-line-medium"></div>
      </div>
    </div>`;
  }

  private messagesLoadMoreSkeletonHtml(): string {
    return `<div class="zento-messages-load-more-skeleton" role="status" aria-busy="true" aria-label="Cargando mensajes anteriores">
      <div class="zento-skeleton zento-skeleton-msg-load-pill" aria-hidden="true"></div>
      <div class="zento-skeleton zento-skeleton-msg-load-pill zento-skeleton-msg-load-pill-short" aria-hidden="true"></div>
    </div>`;
  }

  /**
   * Escape HTML to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // ==========================================================================
  // PAGE CONTEXT EXTRACTION - Universal methods that work on any website
  // ==========================================================================

  /**
   * Extract rich context from the current page.
   * Uses only universal browser APIs that work on any website.
   */
  private extractPageContext(): PageContext {
    const context: PageContext = {
      url: window.location.href,
      pathname: window.location.pathname,
      title: document.title,
      headings: [],
      visibleText: '',
      inputs: [],
      buttons: [],
      links: [],
    };

    try {
      // 1. HEADINGS - Give structure and navigation context
      document.querySelectorAll('h1, h2, h3, h4').forEach((el) => {
        const text = el.textContent?.trim();
        if (text && this.isElementVisible(el)) {
          context.headings.push(text.substring(0, 150));
        }
      });
      context.headings = context.headings.slice(0, 10);

      // 2. VISIBLE TEXT - The content the user sees
      context.visibleText = this.getVisibleText(document.body, 4000);

      // 3. INPUTS - Form fields with their values
      const inputs = document.querySelectorAll('input, select, textarea');
      inputs.forEach((el) => {
        const inputEl = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

        // Skip sensitive or irrelevant types
        const type = (inputEl as HTMLInputElement).type?.toLowerCase() || 'text';
        if (['hidden', 'password', 'file', 'submit', 'button', 'reset', 'image'].includes(type)) {
          return;
        }

        if (!this.isElementVisible(inputEl)) return;

        // Find the label for this field
        const label = this.getInputLabel(inputEl);

        // Get value (handle checkboxes/radios differently)
        let value = '';
        if (type === 'checkbox' || type === 'radio') {
          value = (inputEl as HTMLInputElement).checked ? 'checked' : 'unchecked';
        } else if (inputEl.tagName === 'SELECT') {
          const select = inputEl as HTMLSelectElement;
          value = select.options[select.selectedIndex]?.text || '';
        } else {
          value = inputEl.value || '';
        }

        // Detect validation errors using native API + aria-invalid
        const isInvalid = !inputEl.validity?.valid || inputEl.getAttribute('aria-invalid') === 'true';

        if (label || value) {
          context.inputs.push({
            type,
            label: label.substring(0, 100),
            value: value.substring(0, 300),
            isInvalid,
          });
        }
      });
      context.inputs = context.inputs.slice(0, 25);

      // 4. BUTTONS - Available actions
      const buttons = document.querySelectorAll('button, [role="button"], input[type="submit"], input[type="button"]');
      buttons.forEach((el) => {
        if (!this.isElementVisible(el)) return;

        const text = el.textContent?.trim() ||
          (el as HTMLInputElement).value ||
          el.getAttribute('aria-label') || '';

        if (text && text.length > 1 && text.length < 50) {
          context.buttons.push(text);
        }
      });
      context.buttons = [...new Set(context.buttons)].slice(0, 15);

      // 5. LINKS - Available navigation
      const links = document.querySelectorAll('a[href]');
      links.forEach((el) => {
        if (!this.isElementVisible(el)) return;

        const text = el.textContent?.trim();
        if (text && text.length > 1 && text.length < 80) {
          context.links.push(text);
        }
      });
      context.links = [...new Set(context.links)].slice(0, 15);

    } catch (e) {
      // In case of any errors, return minimal context
      console.warn('[ZentoWidget] Error extracting page context:', e);
    }

    return context;
  }

  /**
   * Get visible text from an element, respecting visibility and skipping non-content elements.
   */
  private getVisibleText(root: Element, maxLength: number): string {
    const chunks: string[] = [];
    let length = 0;

    const walk = (node: Node) => {
      if (length >= maxLength) return;

      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim();
        if (text) {
          chunks.push(text);
          length += text.length;
        }
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const el = node as Element;
      const tag = el.tagName.toLowerCase();

      // Skip non-content elements
      if (['script', 'style', 'noscript', 'template', 'svg', 'path', 'iframe'].includes(tag)) {
        return;
      }

      // Skip hidden elements
      if (!this.isElementVisible(el)) return;

      // Recurse into children
      for (const child of Array.from(node.childNodes)) {
        walk(child);
        if (length >= maxLength) break;
      }
    };

    walk(root);

    return chunks.join(' ').substring(0, maxLength);
  }

  /**
   * Find the label for an input using multiple universal strategies.
   */
  private getInputLabel(input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string {
    // Strategy 1: Explicit <label for="id">
    if (input.id) {
      const label = document.querySelector(`label[for="${input.id}"]`);
      if (label?.textContent?.trim()) {
        return label.textContent.trim();
      }
    }

    // Strategy 2: Wrapping <label>
    const parentLabel = input.closest('label');
    if (parentLabel) {
      // Get label text excluding the input's own text
      const clone = parentLabel.cloneNode(true) as HTMLElement;
      clone.querySelectorAll('input, select, textarea').forEach(el => el.remove());
      if (clone.textContent?.trim()) {
        return clone.textContent.trim();
      }
    }

    // Strategy 3: aria-label
    const ariaLabel = input.getAttribute('aria-label');
    if (ariaLabel) return ariaLabel;

    // Strategy 4: aria-labelledby
    const labelledBy = input.getAttribute('aria-labelledby');
    if (labelledBy) {
      const labelEl = document.getElementById(labelledBy);
      if (labelEl?.textContent?.trim()) {
        return labelEl.textContent.trim();
      }
    }

    // Strategy 5: Placeholder (last resort) - not available on HTMLSelectElement
    if ('placeholder' in input && input.placeholder) return input.placeholder;

    // Strategy 6: name attribute formatted
    if (input.name) {
      return input.name.replace(/[_-]/g, ' ').replace(/([A-Z])/g, ' $1').trim();
    }

    return '';
  }

  /**
   * Check if an element is visible to the user.
   */
  private isElementVisible(el: Element): boolean {
    try {
      // Quick check for common hidden attributes
      if (el.hasAttribute('hidden')) return false;
      if ((el as HTMLElement).style?.display === 'none') return false;
      if (el.getAttribute('aria-hidden') === 'true') return false;

      // Check computed style
      const style = window.getComputedStyle(el);
      if (style.display === 'none') return false;
      if (style.visibility === 'hidden') return false;
      if (style.opacity === '0') return false;

      // Check if element has dimensions
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) return false;

      return true;
    } catch {
      return false;
    }
  }

  // ==========================================================================
  // CONVERSATION LIST MANAGEMENT
  // ==========================================================================

  /**
   * Toggle the conversation list panel
   */
  private toggleConversationList(): void {
    if (this.state.showConversationList) {
      this.hideConversationList();
    } else {
      this.showConversationList();
    }
  }

  /**
   * Show the conversation list panel
   */
  private async showConversationList(): Promise<void> {
    this.state.showConversationList = true;
    if (this.conversationListPanel) {
      this.conversationListPanel.style.display = 'flex';
    }
    // Hide input container when showing conversation list (especially important on mobile)
    if (this.inputContainer) {
      this.inputContainer.style.display = 'none';
    }
    void this.refreshWidgetClientLink();
    if (this.state.inboxTab === 'chats') {
      await this.loadConversations();
    } else {
      await this.loadClientTickets();
    }
    this.updateInboxPanelVisibility();
    this.updateJumpToBottomVisibility();
  }

  /**
   * Hide the conversation list panel
   */
  private hideConversationList(): void {
    this.state.showConversationList = false;
    if (this.conversationListPanel) {
      this.conversationListPanel.style.display = 'none';
    }
    // Show input container when hiding conversation list
    if (this.inputContainer) {
      this.inputContainer.style.display = '';
    }
    this.updateJumpToBottomVisibility();
  }

  /**
   * Load conversations from the API (first page or infinite scroll)
   */
  private async loadConversations(options: { loadMore?: boolean } = {}): Promise<void> {
    const { loadMore = false } = options;
    const itemsContainer = this.conversationItemsContainer;

    if (!itemsContainer) return;

    if (loadMore) {
      if (!this.conversationsHasMore || this.isLoadingMoreConversations) return;
      this.isLoadingMoreConversations = true;
      this.renderConversationList();
      this.wireConversationListLoadMore();
    } else {
      this.disconnectConversationListLoadMore();
      this.state.isLoadingConversations = true;
      itemsContainer.innerHTML = this.conversationListSkeletonHtml();
    }

    try {
      const offset = loadMore ? this.conversationsOffset : 0;
      const response = await this.apiClient.listConversations(this.sessionId, {
        limit: WIDGET_CONVERSATIONS_PAGE_SIZE,
        offset,
      });

      if (loadMore) {
        this.state.conversations = [...this.state.conversations, ...response.conversations];
        this.conversationsOffset = offset + response.conversations.length;
      } else {
        this.state.conversations = response.conversations;
        this.conversationsOffset = response.conversations.length;
      }

      this.conversationsHasMore = response.has_more ?? false;
      this.state.totalUnreadCount = response.total_unread || 0;
      this.state.isLoadingConversations = false;
      this.isLoadingMoreConversations = false;
      this.renderConversationList();
      this.wireConversationListLoadMore();
      this.updateUnreadBadge();
    } catch (error) {
      console.error('[ZentoWidget] Failed to load conversations:', error);
      this.state.isLoadingConversations = false;
      this.isLoadingMoreConversations = false;
      if (!loadMore) {
        itemsContainer.innerHTML = '<div class="zento-conversation-error">Failed to load conversations</div>';
      }
    }
  }

  /**
   * Render the conversation list
   */
  private renderConversationList(): void {
    const itemsContainer = this.conversationItemsContainer;

    if (!itemsContainer || this.state.inboxTab !== 'chats') return;

    if (this.state.conversations.length === 0) {
      itemsContainer.innerHTML = `
        <div class="zento-conversation-empty">
          <div class="zento-empty-icon">${icons.messageCircle}</div>
          <p>No hay conversaciones aún</p>
          <button class="zento-start-chat-btn">Iniciar conversación</button>
        </div>
      `;
      const startBtn = itemsContainer.querySelector('.zento-start-chat-btn');
      startBtn?.addEventListener('click', () => this.startNewConversation());
      return;
    }

    const conversationsHtml = this.state.conversations.map(conv => {
      const isActive = conv.id === this.state.conversationId;
      const timeAgo = conv.last_message_at ? this.formatRelativeTime(new Date(conv.last_message_at)) : '';
      const preview =
        this.toPlainTextPreview(conv.last_message || conv.first_message || '', 60) ||
        'Nueva conversación';
      const unreadCount = conv.unread_count || 0;
      const hasUnread = unreadCount > 0;

      const displayName = 'Soporte';
      const avatarLetter = 'S';

      return `
        <button class="zento-conversation-item ${isActive ? 'zento-active' : ''} ${hasUnread ? 'zento-has-unread' : ''}" data-conversation-id="${conv.id}">
          <div class="zento-conversation-avatar">
            <span class="zento-conversation-avatar-letter">${this.escapeHtml(avatarLetter)}</span>
          </div>
          <div class="zento-conversation-content">
            <div class="zento-conversation-header">
              <span class="zento-conversation-name">${this.escapeHtml(displayName)}</span>
              <span class="zento-conversation-time">${timeAgo}</span>
            </div>
            <div class="zento-conversation-preview">${this.escapeHtml(preview)}</div>
          </div>
          <div class="zento-conversation-arrow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </div>
          ${hasUnread ? '<div class="zento-conversation-unread-dot"></div>' : ''}
        </button>
      `;
    }).join('');

    const loadMoreSentinel =
      this.conversationsHasMore
        ? `<div class="zento-conversation-load-more-sentinel">
          ${this.isLoadingMoreConversations
          ? this.conversationListSkeletonHtml(3, {
            compact: true,
            ariaLabel: 'Cargando más conversaciones',
          })
          : '<div class="zento-conversation-load-more-spacer"></div>'}
        </div>`
        : '';

    itemsContainer.innerHTML = conversationsHtml + loadMoreSentinel;

    // Add click listeners to each conversation item
    const items = itemsContainer.querySelectorAll('.zento-conversation-item');
    items.forEach(item => {
      item.addEventListener('click', () => {
        const conversationId = item.getAttribute('data-conversation-id');
        if (conversationId) {
          this.switchConversation(conversationId);
        }
      });
    });
  }

  /**
   * Switch to a different conversation
   */
  private async switchConversation(conversationId: string): Promise<void> {
    if (conversationId === this.state.conversationId) {
      // Same conversation - just hide list and re-render messages
      // (in case new messages arrived while viewing the list)
      this.hideConversationList();
      this.renderMessages();
      this.markConversationAsRead(conversationId);
      return;
    }

    // Show chat view before loading so the scroll area has real dimensions (list overlay was
    // hiding the thread; scrollToBottom + load-more saw scrollHeight <= clientHeight).
    this.state.messages = [];
    this.canTriggerMessageLoadMore = false;
    this.hideConversationList();
    if (this.messagesContainer) {
      this.messagesContainer.innerHTML = this.messagesThreadSkeletonHtml();
    }

    try {
      const response = await this.apiClient.getConversationMessages(conversationId, {
        fromEnd: true,
        limit: WIDGET_MESSAGES_PAGE_SIZE,
      });

      this.state.conversationId = conversationId;
      const assignmentType = response.assignment_type || 'ai_agent';
      this.state.isHumanMode = assignmentType === 'human_agent';
      this.state.humanRequested = !!response.human_requested_at || assignmentType === 'human_agent';
      this.state.assignedTo = response.assigned_to || null;
      storeConversationId(this.config.apiKey, conversationId);

      this.state.messages = sortChatMessagesForDisplay(response.messages as MessageWithDebug[]);
      this.messagesOffset = response.offset ?? 0;
      this.messagesHasMore = response.has_more ?? false;
      this.renderMessages();
      window.setTimeout(() => {
        this.canTriggerMessageLoadMore = true;
      }, 500);
      this.updateHumanRequestBar();

      // Mark conversation as read
      this.markConversationAsRead(conversationId);

      // Always subscribe to the active conversation so the widget can react
      // when a dashboard agent takes it over without an explicit human request.
      this.startMessageSubscription(conversationId);

    } catch (error) {
      console.error('[ZentoWidget] Failed to load conversation:', error);
      this.showError('Failed to load conversation');
      this.hideConversationList();
    }
  }

  /**
   * Mark a conversation as read and update unread counts
   */
  private async markConversationAsRead(conversationId: string): Promise<void> {
    try {
      await this.apiClient.markConversationAsRead(conversationId, this.sessionId);

      // Update local state - clear unread for this conversation
      const conv = this.state.conversations.find(c => c.id === conversationId);
      if (conv && conv.unread_count) {
        this.state.totalUnreadCount = Math.max(0, this.state.totalUnreadCount - conv.unread_count);
        conv.unread_count = 0;
      } else if (!conv) {
        void this.syncUnreadCountFromServer();
      }

      // Update badge
      this.updateUnreadBadge();

      // Re-render conversation list if visible
      if (this.state.showConversationList) {
        this.renderConversationList();
      }
    } catch (error) {
      // Silently fail - mark as read is not critical
      console.debug('[ZentoWidget] Failed to mark conversation as read:', error);
    }
  }

  /**
   * Start a new conversation
   */
  private startNewConversation(): void {
    // Stop any existing subscription
    this.stopMessageSubscription();

    // Clear current conversation
    this.state.conversationId = null;
    this.state.messages = [];
    this.messagesOffset = 0;
    this.messagesHasMore = false;
    this.canTriggerMessageLoadMore = false;
    this.state.isHumanMode = false;
    this.state.humanRequested = false;
    this.state.assignedTo = null;

    // Set flag to force new conversation on next message
    this.state.forceNewConversation = true;

    // Clear stored conversation ID for this API key
    const key = `zento_conversation_${this.config.apiKey.substring(0, 8)}`;
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore storage errors
    }

    // Show welcome message
    this.showWelcomeMessage();

    // Hide conversation list
    this.hideConversationList();

    // Focus input
    this.inputElement?.focus();
    this.updateHumanRequestBar();
  }

  // ==========================================================================
  // INBOX TABS — CLIENT TICKETS
  // ==========================================================================

  private async refreshWidgetClientLink(): Promise<void> {
    if (!this.hasWidgetUserData()) {
      this.state.hasLinkedClient = false;
      this.state.linkedClientName = null;
      this.state.showDevelopmentTime = false;
      this.updateInboxTabsVisibility();
      if (this.state.inboxTab === 'tickets') {
        void this.setInboxTab('chats');
      }
      return;
    }

    try {
      const link = await this.apiClient.getWidgetClientLink(this.sessionId);
      this.state.hasLinkedClient = link.linked;
      this.state.linkedClientName = link.client_name ?? null;
      this.state.showDevelopmentTime = link.show_development_time ?? false;
      this.updateInboxTabsVisibility();
      if (!link.linked && this.state.inboxTab === 'tickets') {
        void this.setInboxTab('chats');
      }
    } catch (error) {
      console.warn('[ZentoWidget] Failed to resolve CRM client link:', error);
      this.state.hasLinkedClient = false;
      this.state.linkedClientName = null;
      this.state.showDevelopmentTime = false;
      this.updateInboxTabsVisibility();
    }
  }

  private async setInboxTab(tab: WidgetInboxTab): Promise<void> {
    if (tab === 'tickets' && !this.state.hasLinkedClient) {
      return;
    }

    this.state.inboxTab = tab;
    this.updateInboxPanelVisibility();

    if (tab === 'chats') {
      this.closeDevTimeMonthPicker();
      this.disconnectTicketListLoadMore();
      await this.loadConversations();
    } else {
      this.showTicketListSkeleton();
      const loads: Promise<void>[] = [this.loadClientTickets()];
      if (this.state.showDevelopmentTime) {
        loads.push(this.loadWidgetDevTimeByMonth());
      } else {
        this.state.devTimeByMonth = [];
        this.state.isLoadingDevTimeByMonth = false;
        this.closeDevTimeMonthPicker();
      }
      await Promise.all(loads);
    }
  }

  private showTicketListSkeleton(rowCount = 6): void {
    if (!this.ticketItemsContainer) return;
    this.ticketItemsContainer.innerHTML = this.conversationListSkeletonHtml(rowCount, {
      ariaLabel: 'Cargando tickets',
    });
  }

  private updateInboxTabsVisibility(): void {
    if (!this.inboxTabsContainer) return;
    const show = this.state.hasLinkedClient;
    this.inboxTabsContainer.hidden = !show;
    this.inboxTabsContainer.classList.toggle('zento-inbox-panel-hidden', !show);
  }

  private updateInboxPanelVisibility(): void {
    const onChats = this.state.inboxTab === 'chats';

    this.conversationItemsContainer?.classList.toggle('zento-inbox-panel-hidden', !onChats);
    const showDevTime = !onChats && this.state.showDevelopmentTime;
    this.ticketDevUsageContainer?.classList.toggle('zento-inbox-panel-hidden', !showDevTime);
    if (this.ticketDevUsageContainer) {
      this.ticketDevUsageContainer.hidden = !showDevTime;
    }
    this.ticketItemsContainer?.classList.toggle('zento-inbox-panel-hidden', onChats);
    if (this.inboxChatsToolbar) {
      this.inboxChatsToolbar.style.display = onChats ? '' : 'none';
    }

    const titleEl = this.conversationListPanel?.querySelector('.zento-conversation-list-title');
    if (titleEl) {
      titleEl.textContent = onChats ? 'Mensajes' : 'Tickets';
    }

    this.inboxTabsContainer?.querySelectorAll('.zento-inbox-tab').forEach((btn) => {
      const tab = btn.getAttribute('data-inbox-tab');
      btn.classList.toggle('zento-inbox-tab-active', tab === this.state.inboxTab);
    });

    if (!onChats) {
      if (this.state.showDevelopmentTime) {
        this.renderTicketDevUsageBar();
      } else if (this.ticketDevUsageContainer) {
        this.ticketDevUsageContainer.innerHTML = '';
      }
    }
  }

  private async loadClientTickets(options: { loadMore?: boolean } = {}): Promise<void> {
    const { loadMore = false } = options;
    const itemsContainer = this.ticketItemsContainer;
    if (!itemsContainer || !this.state.hasLinkedClient) return;

    if (loadMore) {
      if (!this.ticketsHasMore || this.isLoadingMoreTickets) return;
      this.isLoadingMoreTickets = true;
      this.renderTicketList();
      this.wireTicketListLoadMore();
    } else {
      this.disconnectTicketListLoadMore();
      this.state.isLoadingClientTickets = true;
      this.showTicketListSkeleton();
    }

    try {
      const offset = loadMore ? this.ticketsOffset : 0;
      const response = await this.apiClient.getWidgetTickets(this.sessionId, {
        limit: WIDGET_TICKETS_PAGE_SIZE,
        offset,
      });

      if (loadMore) {
        this.state.clientTickets = [...this.state.clientTickets, ...response.tickets];
        this.ticketsOffset = offset + response.tickets.length;
      } else {
        this.state.clientTickets = response.tickets;
        this.ticketsOffset = response.tickets.length;
      }

      this.ticketsHasMore = response.has_more ?? false;
      this.state.isLoadingClientTickets = false;
      this.isLoadingMoreTickets = false;
      this.renderTicketList();
      this.wireTicketListLoadMore();
    } catch (error) {
      console.error('[ZentoWidget] Failed to load client tickets:', error);
      this.state.isLoadingClientTickets = false;
      this.isLoadingMoreTickets = false;
      if (!loadMore) {
        itemsContainer.innerHTML =
          '<div class="zento-conversation-error">No se pudieron cargar los tickets</div>';
      }
    }
  }

  private formatTicketStatusLabel(status: string): string {
    return TICKET_STATUS_LABELS[status] || status.replace(/_/g, ' ');
  }

  private static currentMonthStartIso(): string {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
  }

  private formatTicketCreatedAt(iso: string | null | undefined): string {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  private formatMonthLabelFromIso(monthIso: string): string {
    const date = new Date(`${monthIso}T12:00:00`);
    if (Number.isNaN(date.getTime())) return monthIso;
    const short = new Intl.DateTimeFormat('es-MX', { month: 'short' }).format(date);
    return `${short} ${date.getFullYear()}`;
  }

  private getDevTimeMonthOptions(): WidgetDevTimeMonthRow[] {
    const current = ZentoWidget.currentMonthStartIso();
    const byMonth = new Map(this.state.devTimeByMonth.map((row) => [row.month, row]));
    if (!byMonth.has(current)) {
      byMonth.set(current, {
        month: current,
        label: this.formatMonthLabelFromIso(current),
        total_minutes: 0,
      });
    }
    return Array.from(byMonth.values()).sort((a, b) => b.month.localeCompare(a.month));
  }

  private getSelectedDevTimeRow(): WidgetDevTimeMonthRow {
    const options = this.getDevTimeMonthOptions();
    const found = options.find((row) => row.month === this.state.selectedDevTimeMonth);
    if (found) return found;
    const current = ZentoWidget.currentMonthStartIso();
    return {
      month: current,
      label: this.formatMonthLabelFromIso(current),
      total_minutes: 0,
    };
  }

  private async loadWidgetDevTimeByMonth(): Promise<void> {
    if (!this.state.hasLinkedClient || !this.state.showDevelopmentTime) return;

    this.state.isLoadingDevTimeByMonth = true;
    this.renderTicketDevUsageBar();

    try {
      const response = await this.apiClient.getWidgetDevelopmentTimeByMonth(this.sessionId);
      this.state.devTimeByMonth = response.months ?? [];
      const current = ZentoWidget.currentMonthStartIso();
      const hasSelected = this.state.devTimeByMonth.some(
        (row) => row.month === this.state.selectedDevTimeMonth,
      );
      if (!hasSelected && this.state.selectedDevTimeMonth !== current) {
        const stillInOptions = this.getDevTimeMonthOptions().some(
          (row) => row.month === this.state.selectedDevTimeMonth,
        );
        if (!stillInOptions) {
          this.state.selectedDevTimeMonth = current;
        }
      }
    } catch (error) {
      console.error('[ZentoWidget] Failed to load development time by month:', error);
      this.state.devTimeByMonth = [];
    } finally {
      this.state.isLoadingDevTimeByMonth = false;
      this.renderTicketDevUsageBar();
    }
  }

  private closeDevTimeMonthPicker(): void {
    if (!this.state.devTimeMonthPickerOpen) return;
    this.state.devTimeMonthPickerOpen = false;
    if (this.devTimeMonthPickerOutsideHandler) {
      document.removeEventListener('mousedown', this.devTimeMonthPickerOutsideHandler);
      this.devTimeMonthPickerOutsideHandler = null;
    }
    this.renderTicketDevUsageBar();
  }

  private openDevTimeMonthPicker(): void {
    if (this.state.devTimeMonthPickerOpen) {
      this.closeDevTimeMonthPicker();
      return;
    }
    this.state.devTimeMonthPickerOpen = true;
    this.renderTicketDevUsageBar();
    this.devTimeMonthPickerOutsideHandler = (event: MouseEvent) => {
      const container = this.ticketDevUsageContainer;
      if (!container) {
        this.closeDevTimeMonthPicker();
        return;
      }
      const clickedInside = event.composedPath().some(
        (node) => node instanceof Node && container.contains(node),
      );
      if (!clickedInside) {
        this.closeDevTimeMonthPicker();
      }
    };
    document.addEventListener('mousedown', this.devTimeMonthPickerOutsideHandler);
  }

  private renderTicketDevUsageBar(): void {
    const container = this.ticketDevUsageContainer;
    if (!container || this.state.inboxTab !== 'tickets') return;

    if (this.state.isLoadingDevTimeByMonth) {
      container.innerHTML = `
        <div class="zento-ticket-dev-usage-inner zento-ticket-dev-usage-loading">
          <span class="zento-ticket-dev-usage-skeleton"></span>
        </div>
      `;
      return;
    }

    const selected = this.getSelectedDevTimeRow();
    const options = this.getDevTimeMonthOptions();
    const usageText = formatDevelopmentMinutes(selected.total_minutes);
    const pickerOpen = this.state.devTimeMonthPickerOpen;

    const monthOptionsHtml = options
      .map((row) => {
        const isActive = row.month === selected.month;
        const rowUsage = formatDevelopmentMinutes(row.total_minutes);
        return `
          <button
            type="button"
            class="zento-ticket-dev-usage-month-option${isActive ? ' zento-ticket-dev-usage-month-option-active' : ''}"
            data-dev-time-month="${this.escapeHtml(row.month)}"
          >
            <span class="zento-ticket-dev-usage-month-option-label">${this.escapeHtml(row.label)}</span>
            <span class="zento-ticket-dev-usage-month-option-value">${this.escapeHtml(rowUsage)}</span>
          </button>
        `;
      })
      .join('');

    container.innerHTML = `
      <div class="zento-ticket-dev-usage-inner">
        <button
          type="button"
          class="zento-ticket-dev-usage-trigger"
          aria-expanded="${pickerOpen ? 'true' : 'false'}"
          aria-haspopup="listbox"
          aria-label="Tiempo de desarrollo, ${this.escapeHtml(selected.label)}: ${this.escapeHtml(usageText)}"
        >
          <span class="zento-ticket-dev-usage-trigger-main">
            <span class="zento-ticket-dev-usage-label">Tiempo de desarrollo</span>
            <span class="zento-ticket-dev-usage-month">${this.escapeHtml(selected.label)}</span>
          </span>
          <span class="zento-ticket-dev-usage-total">${this.escapeHtml(usageText)}</span>
          <span class="zento-ticket-dev-usage-chevron${pickerOpen ? ' zento-ticket-dev-usage-chevron-open' : ''}">${icons.chevronDown}</span>
        </button>
        <div class="zento-ticket-dev-usage-picker" role="listbox" ${pickerOpen ? '' : 'hidden'}>
          ${monthOptionsHtml}
        </div>
      </div>
    `;

    container.querySelector('.zento-ticket-dev-usage-trigger')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.openDevTimeMonthPicker();
    });

    container.querySelectorAll('.zento-ticket-dev-usage-month-option').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        const month = btn.getAttribute('data-dev-time-month');
        if (!month) return;
        this.state.selectedDevTimeMonth = month;
        this.closeDevTimeMonthPicker();
        this.renderTicketDevUsageBar();
      });
    });
  }

  private renderTicketList(): void {
    if (!this.ticketItemsContainer || this.state.inboxTab !== 'tickets') return;

    if (this.state.isLoadingClientTickets && !this.isLoadingMoreTickets) {
      this.showTicketListSkeleton();
      return;
    }

    if (this.state.clientTickets.length === 0) {
      this.ticketItemsContainer.innerHTML = `
        <div class="zento-conversation-empty">
          <div class="zento-empty-icon">${icons.file}</div>
          <p>No hay tickets registrados</p>
        </div>
      `;
      return;
    }

    const ticketsHtml = this.state.clientTickets
      .map((ticket) => {
        const statusLabel = this.formatTicketStatusLabel(ticket.status);
        const createdAt = this.formatTicketCreatedAt(ticket.created_at);
        const devTimeMeta = this.state.showDevelopmentTime
          ? `<span class="zento-ticket-meta-sep" aria-hidden="true">·</span>
              <span class="zento-ticket-dev-time">Tiempo de desarrollo: ${this.escapeHtml(formatDevelopmentMinutes(ticket.development_time_minutes))}</span>`
          : '';
        return `
        <div class="zento-ticket-item" data-ticket-id="${this.escapeHtml(ticket.ticket_id)}">
          <div class="zento-ticket-item-main">
            <div class="zento-ticket-item-header">
              <span class="zento-ticket-label">${this.escapeHtml(ticket.ticket_label)}</span>
              <span class="zento-ticket-status zento-ticket-status-${this.escapeHtml(ticket.status)}">${this.escapeHtml(statusLabel)}</span>
            </div>
            <div class="zento-ticket-title">${this.escapeHtml(ticket.title)}</div>
            <div class="zento-ticket-meta">
              <span class="zento-ticket-created-at">Creado: ${this.escapeHtml(createdAt)}</span>
              ${devTimeMeta}
            </div>
          </div>
          <button type="button" class="zento-ticket-pdf-btn" data-ticket-id="${this.escapeHtml(ticket.ticket_id)}" aria-label="Ver PDF del ticket ${this.escapeHtml(ticket.ticket_label)}">
            PDF
          </button>
        </div>
      `;
      })
      .join('');

    const loadMoreSentinel =
      this.ticketsHasMore
        ? `<div class="zento-ticket-load-more-sentinel">
          ${this.isLoadingMoreTickets
          ? this.conversationListSkeletonHtml(3, {
            compact: true,
            ariaLabel: 'Cargando más tickets',
          })
          : '<div class="zento-conversation-load-more-spacer"></div>'}
        </div>`
        : '';

    this.ticketItemsContainer.innerHTML = ticketsHtml + loadMoreSentinel;

    this.ticketItemsContainer.querySelectorAll('.zento-ticket-pdf-btn').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.stopPropagation();
        const ticketId = btn.getAttribute('data-ticket-id');
        if (ticketId) {
          void this.openTicketPdf(ticketId);
        }
      });
    });
  }

  private ensureTicketPdfOverlay(): HTMLDivElement {
    if (this.ticketPdfOverlay) {
      return this.ticketPdfOverlay;
    }

    const overlay = document.createElement('div');
    overlay.className = 'zento-ticket-pdf-overlay';
    overlay.innerHTML = `
      <div class="zento-ticket-pdf-dialog" role="dialog" aria-modal="true" aria-label="Vista previa del ticket">
        <div class="zento-ticket-pdf-header">
          <span class="zento-ticket-pdf-title">Ticket</span>
          <button type="button" class="zento-ticket-pdf-close" aria-label="Cerrar vista PDF">${icons.x}</button>
        </div>
        <div class="zento-ticket-pdf-body">
          <div class="zento-ticket-pdf-loading" role="status" aria-live="polite">Cargando PDF…</div>
          <iframe class="zento-ticket-pdf-frame" title="PDF del ticket" hidden></iframe>
        </div>
      </div>
    `;
    overlay.querySelector('.zento-ticket-pdf-close')?.addEventListener('click', () => {
      this.closeTicketPdfOverlay();
    });

    this.ensureTicketPdfOverlayStyles();
    document.body.appendChild(overlay);
    this.ticketPdfOverlay = overlay;
    return overlay;
  }

  private ensureTicketPdfOverlayStyles(): void {
    if (this.ticketPdfStyleElement) return;
    const existing = document.getElementById('zento-ticket-pdf-overlay-styles');
    if (existing instanceof HTMLStyleElement) {
      this.ticketPdfStyleElement = existing;
      return;
    }
    const style = document.createElement('style');
    style.id = 'zento-ticket-pdf-overlay-styles';
    style.textContent = createTicketPdfOverlayStyles();
    document.head.appendChild(style);
    this.ticketPdfStyleElement = style;
  }

  private removeTicketPdfOverlayDom(): void {
    this.ticketPdfOverlay?.remove();
    this.ticketPdfOverlay = null;
    if (this.ticketPdfStyleElement) {
      this.ticketPdfStyleElement.remove();
      this.ticketPdfStyleElement = null;
    }
  }

  private setTicketPdfLoading(overlay: HTMLDivElement, isLoading: boolean): void {
    const loadingEl = overlay.querySelector('.zento-ticket-pdf-loading') as HTMLDivElement | null;
    const frame = overlay.querySelector('.zento-ticket-pdf-frame') as HTMLIFrameElement | null;
    if (loadingEl) {
      loadingEl.hidden = !isLoading;
    }
    if (frame) {
      frame.hidden = isLoading;
    }
  }

  private lockTicketPdfBodyScroll(): void {
    if (this.ticketPdfBodyOverflowPrevious !== null) return;
    this.ticketPdfBodyOverflowPrevious = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private unlockTicketPdfBodyScroll(): void {
    if (this.ticketPdfBodyOverflowPrevious === null) return;
    document.body.style.overflow = this.ticketPdfBodyOverflowPrevious;
    this.ticketPdfBodyOverflowPrevious = null;
  }

  private bindTicketPdfEscapeHandler(): void {
    if (this.ticketPdfEscapeHandler) return;
    this.ticketPdfEscapeHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.closeTicketPdfOverlay();
      }
    };
    document.addEventListener('keydown', this.ticketPdfEscapeHandler);
  }

  private unbindTicketPdfEscapeHandler(): void {
    if (!this.ticketPdfEscapeHandler) return;
    document.removeEventListener('keydown', this.ticketPdfEscapeHandler);
    this.ticketPdfEscapeHandler = null;
  }

  private async openTicketPdf(ticketId: string): Promise<void> {
    const ticket = this.state.clientTickets.find((t) => t.ticket_id === ticketId);
    const overlay = this.ensureTicketPdfOverlay();
    const titleEl = overlay.querySelector('.zento-ticket-pdf-title');
    const frame = overlay.querySelector('.zento-ticket-pdf-frame') as HTMLIFrameElement | null;

    if (titleEl) {
      titleEl.textContent = ticket
        ? `${ticket.ticket_label} — ${ticket.title}`
        : 'Ticket';
    }

    this.closeTicketPdfOverlay(false);
    this.setTicketPdfLoading(overlay, true);
    overlay.classList.add('zento-ticket-pdf-overlay-visible');
    this.lockTicketPdfBodyScroll();
    this.bindTicketPdfEscapeHandler();

    try {
      const blob = await this.apiClient.fetchWidgetTicketPdf(this.sessionId, ticketId);
      this.ticketPdfObjectUrl = URL.createObjectURL(blob);
      if (frame) {
        frame.src = this.ticketPdfObjectUrl;
      }
      this.setTicketPdfLoading(overlay, false);
    } catch (error) {
      console.error('[ZentoWidget] Failed to load ticket PDF:', error);
      this.closeTicketPdfOverlay();
      this.showError('No se pudo cargar el PDF del ticket');
    }
  }

  private closeTicketPdfOverlay(clearVisible = true): void {
    if (clearVisible && this.ticketPdfOverlay) {
      this.ticketPdfOverlay.classList.remove('zento-ticket-pdf-overlay-visible');
    }
    const frame = this.ticketPdfOverlay?.querySelector('.zento-ticket-pdf-frame') as HTMLIFrameElement | null;
    if (frame) {
      frame.removeAttribute('src');
      frame.hidden = true;
    }
    if (this.ticketPdfOverlay) {
      this.setTicketPdfLoading(this.ticketPdfOverlay, true);
    }
    if (this.ticketPdfObjectUrl) {
      URL.revokeObjectURL(this.ticketPdfObjectUrl);
      this.ticketPdfObjectUrl = null;
    }
    this.unlockTicketPdfBodyScroll();
    this.unbindTicketPdfEscapeHandler();
  }

  /**
   * Format relative time (e.g., "2h ago", "Yesterday")
   */
  private formatRelativeTime(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  // ==========================================================================
  // WIDGET USER DATA MANAGEMENT
  // ==========================================================================

  /**
   * Load widget user data from localStorage
   */
  private loadWidgetUser(): WidgetUserData | null {
    const storageKey = 'zento_widget_user';
    try {
      const data = localStorage.getItem(storageKey);
      if (data) {
        return JSON.parse(data);
      }
    } catch (e) {
      console.warn('[ZentoWidget] Failed to load widget user from localStorage:', e);
    }
    return null;
  }

  /**
   * Save widget user data to localStorage
   */
  private saveWidgetUser(user: WidgetUserData): void {
    const storageKey = 'zento_widget_user';
    try {
      localStorage.setItem(storageKey, JSON.stringify(user));
    } catch (e) {
      console.warn('[ZentoWidget] Failed to save widget user to localStorage:', e);
    }
  }

  /**
   * Load widget user data from API (async)
   */
  private async loadWidgetUserFromApi(): Promise<void> {
    try {
      const user = await this.apiClient.getWidgetUser(this.sessionId);
      if (user) {
        this.state.widgetUser = {
          name: user.name ?? undefined,
          email: user.email ?? undefined,
        };
        this.saveWidgetUser(this.state.widgetUser);
      }
    } catch {
      // User not found or API error - ignore
    }
    await this.refreshWidgetClientLink();
  }

  /**
   * Whether we have at least an email for handoff / identification (name optional).
   */
  private hasWidgetUserData(): boolean {
    return !!this.state.widgetUser?.email?.trim();
  }

  /**
   * Persist widget user data for the current session
   */
  private async persistWidgetUser(name: string, email: string): Promise<void> {
    const userData: WidgetUserData = { name, email };

    // Persist locally first so the widget can reuse the data immediately.
    this.state.widgetUser = userData;
    this.saveWidgetUser(userData);

    try {
      const savedUser = await this.apiClient.updateWidgetUser(this.sessionId, userData);
      this.state.widgetUser = {
        name: savedUser.name ?? undefined,
        email: savedUser.email ?? undefined,
      };
      this.saveWidgetUser(this.state.widgetUser);
      await this.refreshWidgetClientLink();
    } catch (error) {
      console.warn('[ZentoWidget] Failed to sync widget user to API:', error);
    }
  }

  /**
   * Handle a click on the human request action
   */
  private async handleHumanRequestClick(): Promise<void> {
    if (this.hasWidgetUserData() && this.state.widgetUser) {
      const email = this.state.widgetUser.email!.trim();
      const name =
        this.state.widgetUser.name?.trim() ||
        email.split('@')[0] ||
        'Usuario';
      await this.requestHuman(name, email);
      return;
    }

    this.showWidgetUserModal('human');
  }

  /**
   * Show the widget user data modal
   */
  private showWidgetUserModal(mode: 'conversation' | 'human' = 'human'): void {
    if (!this.shadowRoot) return;

    // Check if modal already exists
    let modal = this.shadowRoot.querySelector('.zento-human-modal') as HTMLDivElement;
    if (modal) return;

    const primaryColor = this.state.assistantInfo?.primary_color || '#6366f1';
    const existingUser = this.state.widgetUser;
    const isConversationMode = mode === 'conversation';
    const title = isConversationMode ? 'Antes de comenzar' : 'Hablar con un humano';
    const subtitle = isConversationMode
      ? 'Guarda tu nombre y correo desde ahora para identificar tu conversación y no volver a pedirlos si luego necesitas ayuda humana.'
      : 'Para conectarte con nuestro equipo, necesitamos tus datos de contacto.';
    const submitLabel = isConversationMode ? 'Guardar datos' : 'Conectar con agente';
    const modalIcon = isConversationMode ? icons.user : icons.headset;

    modal = document.createElement('div');
    modal.className = 'zento-human-modal';
    modal.innerHTML = `
      <div class="zento-human-modal-backdrop"></div>
      <div class="zento-human-modal-content">
        <div class="zento-human-modal-header">
          <div class="zento-human-modal-icon" style="background: ${primaryColor}20;">
            ${modalIcon}
          </div>
          <h3 class="zento-human-modal-title">${title}</h3>
          <p class="zento-human-modal-subtitle">${subtitle}</p>
        </div>
        <form class="zento-human-modal-form">
          <div class="zento-human-modal-field">
            <label for="zento-human-name">
              ${icons.user}
              Nombre
            </label>
            <input 
              type="text" 
              id="zento-human-name" 
              placeholder="Tu nombre completo"
              value="${this.escapeHtml(existingUser?.name || '')}"
              required
            />
          </div>
          <div class="zento-human-modal-field">
            <label for="zento-human-email">
              ${icons.mail}
              Correo electrónico
            </label>
            <input 
              type="email" 
              id="zento-human-email" 
              placeholder="tu@email.com"
              value="${this.escapeHtml(existingUser?.email || '')}"
              required
            />
          </div>
          <div class="zento-human-modal-actions">
            <button type="button" class="zento-human-modal-cancel">Cancelar</button>
            <button type="submit" class="zento-human-modal-submit" style="background: ${primaryColor};">
              ${submitLabel}
            </button>
          </div>
        </form>
      </div>
    `;

    this.shadowRoot.appendChild(modal);

    // Event listeners
    const backdrop = modal.querySelector('.zento-human-modal-backdrop') as HTMLDivElement;
    const cancelBtn = modal.querySelector('.zento-human-modal-cancel') as HTMLButtonElement;
    const form = modal.querySelector('.zento-human-modal-form') as HTMLFormElement;

    const closeModal = () => {
      modal.classList.add('zento-closing');
      setTimeout(() => modal.remove(), 200);

      if (isConversationMode && !this.hasWidgetUserData()) {
        void this.showConversationList();
      }
    };

    backdrop?.addEventListener('click', closeModal);
    cancelBtn?.addEventListener('click', closeModal);

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const nameInput = form.querySelector('#zento-human-name') as HTMLInputElement;
      const emailInput = form.querySelector('#zento-human-email') as HTMLInputElement;

      const name = nameInput?.value.trim();
      const email = emailInput?.value.trim();

      if (!name || !email) {
        this.showError('Por favor completa todos los campos');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.showError('Por favor ingresa un correo válido');
        return;
      }

      closeModal();

      if (isConversationMode) {
        await this.persistWidgetUser(name, email);
        return;
      }

      await this.requestHuman(name, email);
    });
  }

  /**
   * Request to speak with a human agent
   */
  private async requestHuman(name: string, email: string): Promise<void> {
    if (!this.state.conversationId) {
      this.showError('Primero inicia una conversación');
      return;
    }

    try {
      // Show loading
      this.showTypingIndicator('Conectando con un agente...');

      const response = await this.apiClient.requestHuman(
        this.sessionId,
        this.state.conversationId,
        name,
        email
      );

      // Save user data
      const userData: WidgetUserData = { name, email };
      this.state.widgetUser = userData;
      this.saveWidgetUser(userData);

      // Mark as human requested
      this.state.humanRequested = true;
      this.state.isHumanMode = true;
      this.state.assignedTo = null;

      // Hide typing indicator
      this.hideTypingIndicator();

      // Add system message
      const systemMessage: MessageWithDebug = {
        id: `system_${Date.now()}`,
        role: 'assistant',
        content: `✅ Tu solicitud ha sido enviada. Un agente de nuestro equipo te responderá pronto a través de este chat.`,
      };
      this.state.messages.push(systemMessage);
      this.appendMessage(systemMessage);

      console.log('[ZentoWidget] Human requested successfully:', response);

      // Start subscription to receive human agent messages
      this.startMessageSubscription(this.state.conversationId);
      this.updateHumanRequestBar();

    } catch (error: unknown) {
      console.error('[ZentoWidget] Failed to request human:', error);
      this.hideTypingIndicator();
      let detail: string | undefined;
      if (typeof error === 'object' && error !== null && 'data' in error) {
        const data = (error as { data?: { detail?: unknown } }).data;
        if (typeof data?.detail === 'string') detail = data.detail;
      }
      this.showError(detail || 'Error al solicitar agente. Intenta de nuevo.');
    }
  }

  /**
   * Start subscribing to real-time messages for a conversation
   * Used when conversation is handled by a human agent
   */
  private startMessageSubscription(conversationId: string): void {
    // Stop any existing subscription
    this.stopMessageSubscription();

    console.log('[ZentoWidget] Starting message subscription for:', conversationId);

    const realtimeConfig = this.state.assistantInfo?.realtime_config || null;
    if (!realtimeConfig) {
      console.warn('[ZentoWidget] Realtime config missing, skipping subscription');
      return;
    }

    this.subscriptionCleanup = subscribeConversationRealtime(
      conversationId,
      realtimeConfig,
      {
        onNewMessage: (message) => {
          console.log('[ZentoWidget] New message received:', message.role, message.content?.substring(0, 50));

          // Skip user messages - they're already added locally when sent
          // This prevents duplicates since local messages have temp IDs
          if (message.role === 'user') {
            return;
          }

          // Skip assistant messages from realtime - they always arrive via SSE streaming
          // This prevents duplicates when the backend broadcasts AI responses
          // Realtime should only handle human_agent messages
          if (message.role === 'assistant') {
            console.log('[ZentoWidget] Ignoring assistant message from realtime (handled via SSE streaming)');
            return;
          }

          // Only add messages we don't already have
          const exists = this.state.messages.some(m => m.id === message.id);
          if (!exists) {
            // Add message to state (including attachments if present)
            const newMessage: MessageWithDebug = {
              id: message.id,
              role: message.role,
              content: message.content,
              sent_by_user_id: message.sent_by_user_id,
              sent_by_name:
                message.role === 'human_agent' ? 'Soporte' : message.sent_by_name,
              attachments: message.attachments,
            };
            this.state.messages.push(newMessage);
            if (message.role === 'human_agent') {
              this.state.isHumanMode = true;
              this.state.humanRequested = true;
              if (message.sent_by_user_id) {
                this.state.assignedTo = message.sent_by_user_id;
              }
              this.updateHumanRequestBar();
            }

            if (this.isUserActivelyViewingConversation(conversationId)) {
              this.appendMessage(newMessage);
              void this.markConversationAsRead(conversationId);
            }
            // When not viewing this thread, unread + toast are handled only by the session
            // subscription (see startSessionSubscription) so we never double-count when both
            // channels receive the same broadcast.

            // Callback
            if (message.role === 'human_agent') {
              this.config.onMessageReceived(message.content);
            }
          }
        },

        onAssignmentChanged: (assignmentType, assignedTo) => {
          console.log('[ZentoWidget] Assignment changed to:', assignmentType);

          // Keep the subscription active for the current conversation so future
          // dashboard takeovers also arrive in real time.
          if (assignmentType === 'ai_agent') {
            this.state.isHumanMode = false;
            this.state.humanRequested = false;
            this.state.assignedTo = null;

            // Add system message
            const systemMessage: MessageWithDebug = {
              id: `system_${Date.now()}`,
              role: 'assistant',
              content: '🤖 La conversación ha sido transferida de vuelta al asistente AI.',
            };
            this.state.messages.push(systemMessage);
            this.appendMessage(systemMessage);
          } else if (assignmentType === 'human_agent') {
            this.state.isHumanMode = true;
            this.state.humanRequested = true;
            this.state.assignedTo = assignedTo || null;
          }
          this.updateHumanRequestBar();
        },

        onError: (error) => {
          console.error('[ZentoWidget] Subscription error:', error);
          // Don't stop subscription on error - it may recover
        },
      },
    );
  }

  /**
   * Stop the message subscription
   */
  private stopMessageSubscription(): void {
    if (this.subscriptionCleanup) {
      console.log('[ZentoWidget] Stopping message subscription');
      this.subscriptionCleanup();
      this.subscriptionCleanup = null;
    }
  }

  /**
   * Start session-level subscription for global notifications.
   * This listens for messages across ALL conversations for this session.
   * Used to show notifications when widget is closed or user is in another conversation.
   */
  private startSessionSubscription(): void {
    // Stop any existing session subscription
    this.stopSessionSubscription();

    const realtimeConfig = this.state.assistantInfo?.realtime_config || null;
    if (!realtimeConfig) {
      console.warn('[ZentoWidget] Realtime config missing, skipping session subscription');
      return;
    }

    console.log('[ZentoWidget] Starting session subscription for:', this.sessionId);

    this.sessionSubscriptionCleanup = subscribeSessionRealtime(
      this.sessionId,
      realtimeConfig,
      {
        onNewMessage: (message: SessionMessage, conversationId: string) => {
          console.log('[ZentoWidget] Session message received:', message.role, conversationId);

          // Skip user messages
          if (message.role === 'user') {
            return;
          }

          // API unread counts assistant + human_agent; same rules here
          if (message.role !== 'assistant' && message.role !== 'human_agent') {
            return;
          }

          if (this.isUserActivelyViewingConversation(conversationId)) {
            // Thread is on screen — conversation subscription updates UI; no badge/toast
            return;
          }

          if (!this.tryConsumeRealtimeNotificationDedupe(conversationId, message.id)) {
            return;
          }

          console.log('[ZentoWidget] Showing notification for message in conversation:', conversationId);

          this.state.totalUnreadCount++;
          this.updateUnreadBadge();

          this.showNewMessageToast(message.content, conversationId);

          if (this.state.showConversationList) {
            void this.loadConversations();
          }
        },

        onError: (error) => {
          console.error('[ZentoWidget] Session subscription error:', error);
        },
      },
    );
  }

  /**
   * Stop the session-level subscription
   */
  private stopSessionSubscription(): void {
    if (this.sessionSubscriptionCleanup) {
      console.log('[ZentoWidget] Stopping session subscription');
      this.sessionSubscriptionCleanup();
      this.sessionSubscriptionCleanup = null;
    }
  }

  /**
   * Remove widget from DOM
   */
  destroy(): void {
    // Stop subscriptions
    this.stopMessageSubscription();
    this.stopSessionSubscription();
    this.disconnectConversationListLoadMore();
    this.disconnectTicketListLoadMore();
    this.closeTicketPdfOverlay();
    this.removeTicketPdfOverlayDom();
    if (this.jumpToBottomResizeObserver) {
      this.jumpToBottomResizeObserver.disconnect();
      this.jumpToBottomResizeObserver = null;
    }

    if (this.container) {
      this.container.remove();
      this.container = null;
    }
    this.shadowRoot = null;
    this.widgetRoot = null;
    this.styleElement = null;
    this.chatWindow = null;
    this.messagesContainer = null;
    this.inputElement = null;
  }

  /**
   * Update widget configuration dynamically
   */
  updateConfig(config: Partial<ZentoWidgetConfig>): void {
    Object.assign(this.config, config);

    // Re-inject styles if color changed
    if (config.baseUrl || this.styleElement) {
      this.styleElement?.remove();
      this.styleElement = null;
      this.injectStyles();
    }
  }

  /**
   * Get current state (for debugging)
   */
  getState(): Readonly<WidgetState> {
    return { ...this.state };
  }
}


